import { Fragment, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { DatePicker, TimeField } from '@mui/x-date-pickers';
import { Breadcrumbs, Typography, Box, FormControl, Select, Tabs, Tab, MenuItem, Paper, FormControlLabel, RadioGroup, Radio, IconButton, InputBase, Divider, InputLabel, TableContainer, TableBody, TableRow, TableCell, Table, FormHelperText, OutlinedInput, TextField, Checkbox, Modal, Tooltip } from '@mui/material';
import dayjs from '../../../../config/dayjs';
import PropTypes from 'prop-types';
import { useToaster } from '../../../../providers/ToasterProvider';
import { listByDriveAndCourse } from '../../../../services/masterService';
import { create, getDetailsWithQuestionsStatusById, updateById, updateLockedExamConfig } from '../../../../services/configurationService';
import { AuthContext } from '../../../../providers/auth';
import { MainContainer, Container } from '../../../../components';
import { Menu } from '../Menu/Menu';
import languages from '../../../../constant/languages';
import mimes from '../../../../constant/mimes';
import defaultViolations from '../../../../constant/violations';
import APP_CONSTANTS from '../../../../constant/app';
import { getAll as getAllViolations } from '../../../../services/violationService';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import RadiusButton from '../../../../components/RadiusButton';
import TRUE_BLACK from '../../../../assets/images/true-black.svg';
import ARROW_LEFT from '../../../../assets/images/back.svg';
import DELETE_ICON from '../../../../assets/images/action-delete.svg';
import ACTION_DOWNLOAD from '../../../../assets/images/download-blue.svg';

import './ContentConfiguration.scss';
import { LoadingButton } from '@mui/lab';
import { ProjectContext } from '../../../../providers/projectProvider';

function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
            style={{ height: '100%', overflowY: 'auto' }}
        >
            {value === index && <Box sx={{ p: 0, bgcolor: 'background.base' }}>{children}</Box>}
        </div>
    );
}

function a11yProps(index) {
    return {
        id: `configuration-tab-${index}`,
        'aria-controls': `configuration-tabpanel-${index}`,
    };
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

const DEFAULT_NEW_PROCTORING_VIOLATIONS = ['WARN_HW_MF', 'WARN_HW_NF', 'WARN_OD_CPD'];

// Security-level / KMS errors from the backend carry a user-facing message.
const SECURITY_ERROR_CODES = ['SECURITY_LEVEL_NOT_SUPPORTED', 'SECURITY_LEVEL_NOT_ALLOWED_FOR_EXAM_TYPE', 'CLIENT_NOT_FOUND', 'KMS_PROVIDER_NOT_CONFIGURED', 'KMS_SERVICE_NOT_CONFIGURED', 'DATA_ENCRYPTION_KEY_NOT_FOUND'];
const getSecurityErrorMessage = (error) => {
    const data = error?.response?.data;
    const isSecurityError = SECURITY_ERROR_CODES.includes(data?.code) || data?.code?.startsWith?.('KMS_');
    return isSecurityError ? data.message : null;
};

export const ContentConfiguration = () => {

    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();
    const projectName = params.projectName;
    const { addNotification } = useToaster();
    const { projectDetails } = useContext(ProjectContext);
    
    const [configurationDetails, setConfigurationDetails] = useState(null);
    const [drive, setDrive] = useState({
        id: null,
        driveName: 'Drive name'
    });
    const [course, setCourse] = useState({
        id: null,
        courseName: 'Course name'
    });

    const [isLocked, setIsLocked] = useState(false);
    const [contentUploaded, setContentUploaded] = useState(false);
    const [isExamEnabled, setIsExamEnabled] = useState(false);
    const [canEditLockedConfig, setCanEditLockedConfig] = useState(true);
    const [isMultiDayExam, setIsMultiDayExam] = useState(false);
    const canEditNow = (isLocked || isExamEnabled) && (isMultiDayExam || canEditLockedConfig);
    // Security settings are set only when a configuration is created; the update API ignores them.
    const securitySettingsLocked = Boolean(location?.state?.configurationId);
    const [configuration, setConfiguration] = useState({
        examType: 'MCQ',
        timeDuration: dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
        timeDurationForScribe: dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
        startExamBetweenSpecifiedTime: false,
        examTimes: [],
        unlockWithPassword: 'No',
        proctoringEnabled: 'No',
        candidateAuthorizationEnabled: 'No',
        autoLogout: 'Yes',
        submitButtonDisplayMinutes: dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
        calculator: 'No',
        provideGraph: 'No',
        logarithm: 'No',
        calculatorType: 'normal',
        feedback: 'No',
        numberOfFeedbackQuestions: 1,
        sectionWise: 'No',
        selectionType: 'fixed',
        sectionNavigation: 'NO_MOVEMENT_OR_TIME_RESTRICTION',
        sectionCount: 1,
        sections: [{
            sectionTitle: 'Section 1',
            noOfQuestions: '',
            sectionTime: dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
            subSectionsCount: 0,
            subSections: []
        }],
        contentUploadMethod: 'set',
        numberOfEasyQuestions: 0,
        numberOfMediumQuestions: 0,
        numberOfHardQuestions: 0,
        numberOfSets: 1,
        questionsPerSet: 1,
        maxMarks: 1,
        randomizeQuestions: 'No',
        randomizeOptions: 'No',
        isBilingual: 'No',
        languages: [],
        instructionSheets: 'No',
        biometricRequired: 'Yes',
        // biometricMethod: 'photo',
        biometricMethod: [],
        sampleTestRequired: 'No',
        allQuestionsMandatory: 'No',
        questionNavigationForwardOnly: 'No',
        negativeMarking: 'No',
        numberOfAnswerPages: 1,
        maximumLoginAttempts: 'No',
        maximumLoginAttemptsCount: 1,
        lateLoginAllowed: 'No',
        lateLoginMinutes: dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
        loginBeforeMinutes: dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
        reminders: [],
        forcedLogout: 'No',
        instantScore: 'No',
        instantResult: 'No',
        passingMarks: null,
        allowUpload: 'No',
        uploadType: null,
        uploadDuration: dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
        isSafeExamBrowser: false,
        isSecureFolder: false,
        folderType: null,
        whitelistedFolderPathWindows: '',
        whitelistedFolderPathMac: '',
        selectedViolations: [],
        isViolationAutoSubmitEnabled: 'No',
        violationThresholdCount: 0,
        triggerViolations: [],
        aiAlertBeepEnabled: 'No',
        securityLevel: 'NONE',
        dataEncryptionKeyScope: null,
        createConfigurationKmsKey: 'No',
    });
    const [documentFiles, setDocumentFiles] = useState({
        logTablesFile: null,
        signedLogTablesFile: null,
        removeLogTablesFile: false,
        pvChartsFile: null,
        signedPvChartsFile: null,
        removePvChartsFile: false,
        heatTableFile: null,
        signedHeatTableFile: null,
        removeHeatTableFile: false,
        helpDocumentFile: null,
        signedHelpDocumentFile: null,
        removeHelpDocumentFile: false,
    });
    const [tabValue, setTabValue] = useState(0);
    const [sectionCount, setSectionCount] = useState(1);
    const [remindersCount, setRemindersCount] = useState(0);
    const [languagesError, setLanguagesError] = useState(null);
    const [biometricMethodError, setBiometricMethodError] = useState(null);
    const [errors, setErrors] = useState({
        timeDuration: null,
        timeDurationForScribe: null,
        examTimes: [],
        submitButtonDisplayMinutes: null,
        numberOfFeedbackQuestions: null,
        maximumLoginAttemptsCount: null,
        sections: [{
            timeError: null,
            titleError: null,
            countError: null,
            subSections: []
        }],
        sectionCount: null,
        maxMarks: null,
        sectionError: null,
        numberOfSets: null,
        numberOfEasyQuestions: null,
        numberOfMediumQuestions: null,
        numberOfHardQuestions: null,
        numberOfQuestions: null,
        lowQuestions: null,
        loginBeforeMinutes: null,
        lateLoginMinutes: null,
        numberOfAnswerPages: null,
        remindersCount: null,
        reminders: [],
        passingMarks: null,
        uploadDuration: null,
        whitelistedFolderPathWindows: null,
        whitelistedFolderPathMac: null,
    });
    const [isSaving, setIsSaving] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [lockedSaveConfirmVisible, setLockedSaveConfirmVisible] = useState(false);
    const [availableViolations, setAvailableViolations] = useState([]);
    const [violationErrors, setViolationErrors] = useState({
        selectedViolations: null,
        violationThresholdCount: null,
        triggerViolations: null,
    });

    useEffect(() => {
        let isDisabled = !!languagesError;
        if (errors.timeDuration || errors.timeDurationForScribe || errors.submitButtonDisplayMinutes || errors.sectionError || errors.numberOfSets || errors.numberOfQuestions || errors.lowQuestions || errors.loginBeforeMinutes || errors.lateLoginMinutes || errors.uploadDuration) {
            isDisabled = true;
        }
        for (let examTimeError of errors.examTimes) {
            if (examTimeError.startTime || examTimeError.endTime) {
                isDisabled = true;
            }
        }
        for (let sectionError of errors.sections) {
            if (sectionError.timeError || sectionError.titleError || sectionError.countError) {
                isDisabled = true;
            }
            if (sectionError.subSections) {
                for (let subSectionError of sectionError.subSections) {
                    if (subSectionError) {
                        isDisabled = true;
                    }
                }
            }
        }
        for (let reminderError of errors.reminders) {
            if (reminderError) {
                isDisabled = true;
            }
        }
        setHasError(isDisabled);
    }, [errors])

    const goToMasters = () => {
        if (projectName) {
            navigate(`/${projectName}/masters`);
        }
    }

    const handleFileChange = (target, documentName) => {
        const file = target?.files[0];
        if (!file) return;
        if (mimes.CONFIGURATION_DOCUMENT_ALLOWED_TYPES.includes(file.type)) {
            setDocumentFiles({
                ...documentFiles,
                [documentName]: file,
            });
        }
    }
    const handleFileRemove = (documentName) => {
        const element = document.getElementById(documentName);
        if (element) {
            element.value = '';
            setDocumentFiles({
                ...documentFiles,
                [documentName]: null,
            });
        }
    }
    const handleDownloadFile = (fieldName) => {
        const downloadUrl = documentFiles[fieldName];
        if (!downloadUrl) return;
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.target = '_blank';
        link.setAttribute('download', 'document.pdf');
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    }
    const handleRemoveLogTables = () => {
        setDocumentFiles({
            ...documentFiles,
            removeLogTablesFile: true,
            signedLogTablesFile: null
        });
    }
    const handleRemovePvCharts = () => {
        setDocumentFiles({
            ...documentFiles,
            removePvChartsFile: true,
            signedPvChartsFile: null
        });
    }
    const handleRemoveHeatTable = () => {
        setDocumentFiles({
            ...documentFiles,
            removeHeatTableFile: true,
            signedHeatTableFile: null
        });
    }
    const handleRemoveHelpDocument = () => {
        setDocumentFiles({
            ...documentFiles,
            removeHelpDocumentFile: true,
            signedHelpDocumentFile: null
        });
    }

    useEffect(() => {
        const driveDetails = location?.state?.drive;
        const courseDetails = location?.state?.course;
        const configurationId = location?.state?.configurationId;
        if (!driveDetails || !courseDetails) {
            goToMasters();
        } else {
            setDrive(() => driveDetails);
            setCourse(() => courseDetails);
            const getBatches = async () => {
                try {
                    const res = await listByDriveAndCourse(driveDetails.id, courseDetails.id);
                    if (res.code === 'OK') {
                        const batches = res.data.list.map((pm) => {
                            const examDate = dayjs.utc(pm.examDate).hour(0).minute(0).second(0).millisecond(0);
                            return {
                                projectMasterId: pm.id,
                                batchId: pm.batchId,
                                batchName: pm['batch.batchName'],
                                startTime: examDate.clone(),
                                endTime: examDate.clone(),
                            }
                        })
                        if (configurationId) {
                            const fetchDetails = async () => {
                                try {
                                    const res = await getDetailsWithQuestionsStatusById(configurationId);
                                    if (res.code === 'OK') {
                                        const data = res.data;
                                        setIsLocked(data.isLocked);
                                        setContentUploaded(data.contentUploaded);
                                        setIsExamEnabled(data.examEnabled);
                                        setCanEditLockedConfig(data.canEditLockedConfig ?? true);
                                        setIsMultiDayExam(data.startExamBetweenSpecifiedTime ?? false);
                                        const dataToSet = {
                                            examType: data.examType,
                                            timeDuration: dayjs.utc().hour(data.timeDuration.split(':')[0]).minute(data.timeDuration.split(':')[1]).second(0).millisecond(0),
                                            timeDurationForScribe: dayjs.utc().hour(data.timeDurationForScribe.split(':')[0]).minute(data.timeDurationForScribe.split(':')[1]).second(0).millisecond(0),
                                            startExamBetweenSpecifiedTime: data.startExamBetweenSpecifiedTime,
                                            examTimes: [],
                                            unlockWithPassword: data.unlockWithPassword ? 'Yes' : 'No',
                                            proctoringEnabled: data.proctoringEnabled ? 'Yes' : 'No',
                                            ...(data.proctoringEnabled && { proctoringType: data.proctoringType, proctoringEngine: data.proctoringEngine }),
                                            candidateAuthorizationEnabled: data.proctoringEnabled && data.candidateAuthorizationEnabled ? 'Yes' : 'No',
                                            selectedViolations: data.proctoringEnabled && !data.selectedViolations?.length
                                                ? DEFAULT_NEW_PROCTORING_VIOLATIONS
                                                : data.selectedViolations?.map(v => v.warningCode) ?? [],
                                            isViolationAutoSubmitEnabled: data.isViolationAutoSubmitEnabled ? 'Yes' : 'No',
                                            violationThresholdCount: data.violationThresholdCount ?? 0,
                                            triggerViolations: data.triggerViolations?.map(v => v.warningCode) ?? [],
                                            aiAlertBeepEnabled: data.aiAlertBeepEnabled ? 'Yes' : 'No',
                                            securityLevel: data.securityLevel ?? 'NONE',
                                            dataEncryptionKeyScope: data.dataEncryptionKeyScope ?? (data.securityLevel && data.securityLevel !== 'NONE' ? 'EXAM' : null),
                                            createConfigurationKmsKey: data.createConfigurationKmsKey ? 'Yes' : 'No',
                                            autoLogout: data.autoLogout ? 'Yes' : 'No',
                                            submitButtonDisplayMinutes: data.autoLogout ? dayjs.utc().hour(0).minute(0).second(0).millisecond(0) : dayjs.utc().hour(Math.floor(data.submitButtonDisplayMinutes / 60)).minute(data.submitButtonDisplayMinutes % 60).second(0).millisecond(0),
                                            calculator: data.calculator === 'none' ? 'No' : 'Yes',
                                            calculatorType: data.calculator === 'scientific' ? 'scientific' : 'normal',
                                            provideGraph: data.provideGraph ? 'Yes' : 'No',
                                            logarithm: data.logarithm ? 'Yes' : 'No',
                                            feedback: data.feedback ? 'Yes' : 'No',
                                            numberOfFeedbackQuestions: data.numberOfFeedbackQuestions ? data.numberOfFeedbackQuestions : 1,
                                            sectionWise: data.sectionWise ? 'Yes' : 'No',
                                            languages: data.languages,
                                            contentUploadMethod: data.contentUploadMethod,
                                            numberOfEasyQuestions: data.contentUploadMethod === 'bulk' ? data.numberOfEasyQuestions : 0,
                                            numberOfMediumQuestions: data.contentUploadMethod === 'bulk' ? data.numberOfMediumQuestions : 0,
                                            numberOfHardQuestions: data.contentUploadMethod === 'bulk' ? data.numberOfHardQuestions : 0,
                                            numberOfSets: data.numberOfSets,
                                            questionsPerSet: data.questionsPerSet,
                                            maxMarks: data.examType !== 'TYPING' ? data.maxMarks : 1,
                                            randomizeQuestions: data.examType === 'MCQ' && data.randomizeQuestions ? 'Yes' : 'No',
                                            randomizeOptions: data.examType === 'MCQ' && data.randomizeOptions ? 'Yes' : 'No',
                                            isBilingual: data.isBilingual ? 'Yes' : 'No',
                                            instructionSheets: data.instructionSheets ? 'Yes' : 'No',
                                            biometricRequired: data.biometricRequired ? 'Yes' : 'No',
                                            biometricMethod: data.biometricMethod,
                                            sampleTestRequired: data.sampleTestRequired ? 'Yes' : 'No',
                                            allQuestionsMandatory: data.allQuestionsMandatory ? 'Yes' : 'No',
                                            questionNavigationForwardOnly: data.questionNavigationForwardOnly ? 'Yes' : 'No',
                                            negativeMarking: data.negativeMarking ? 'Yes' : 'No',
                                            numberOfAnswerPages: data.examType === 'DESCRIPTIVE' ? data.numberOfAnswerPages : 1,
                                            maximumLoginAttempts: !isNaN(parseInt(data.maximumLoginAttempts)) ? 'Yes' : 'No',
                                            maximumLoginAttemptsCount: !isNaN(parseInt(data.maximumLoginAttempts)) ? parseInt(data.maximumLoginAttempts) : 1,
                                            loginBeforeMinutes: dayjs().utc().hour(data.loginBeforeMinutes.split(':')[0]).minute(data.loginBeforeMinutes.split(':')[1]).second(0).millisecond(0),
                                            reminders: data.reminders.map((reminder) => {
                                                return dayjs().utc().hour(reminder.split(':')[0]).minute(reminder.split(':')[1]).second(0).millisecond(0);
                                            }),
                                            forcedLogout: data.forcedLogout ? 'Yes' : 'No',
                                            lateLoginAllowed: data.lateLoginAllowed ? 'Yes' : 'No',
                                            lateLoginMinutes: dayjs().utc().hour(Math.floor(data.lateLoginMinutes / 60)).minute(data.lateLoginMinutes % 60).second(0).millisecond(0),
                                            instantScore: data.instantScore ? 'Yes' : 'No',
                                            instantResult: data.instantResult ? 'Yes' : 'No',
                                            passingMarks: data.passingMarks ?? null,
                                            selectionType: data.selectionType || 'fixed',
                                            allowUpload: data.allowUpload ? 'Yes' : 'No',
                                            uploadType: data.uploadType || null,
                                            uploadDuration: data.uploadDurationInMinutes
                                                ? dayjs.utc().hour(Math.floor(data.uploadDurationInMinutes / 60)).minute(data.uploadDurationInMinutes % 60).second(0).millisecond(0)
                                                : dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
                                            isSafeExamBrowser: data.isSafeExamBrowser ?? false,
                                            isSecureFolder: data.isSecureFolder ?? false,
                                            folderType: data.folderType ?? null,
                                            whitelistedFolderPathWindows: data.whitelistedFolderPathWindows ?? '',
                                            whitelistedFolderPathMac: data.whitelistedFolderPathMac ?? '',
                                        }
                                        setRemindersCount(data.reminders.length);

                                        for (let batch of batches) {
                                            const existingBatch = data.examTimes.findIndex((examTime) => examTime.projectMasterId === batch.projectMasterId);
                                            let examTime = {
                                                projectMasterId: batch.projectMasterId,
                                                batchId: batch.batchId,
                                                batchName: batch.batchName,
                                            }
                                            if (existingBatch > -1) {
                                                examTime.startTime = dayjs.utc(data.examTimes[existingBatch].startTime);
                                                examTime.endTime = dayjs.utc(data.examTimes[existingBatch].endTime);
                                                examTime.hasExamConfig = data.examTimes[existingBatch].hasExamConfig ?? false;
                                            } else {
                                                examTime.startTime = batch.startTime;
                                                examTime.endTime = batch.endTime;
                                                examTime.hasExamConfig = false;
                                            }
                                            dataToSet.examTimes.push(examTime);
                                        }

                                        if (data.sectionWise) {
                                            setSectionCount(data.sectionCount);
                                            dataToSet.sectionCount = data.sectionCount;
                                            dataToSet.sectionNavigation = data.sectionNavigation;
                                            dataToSet.sections = data.sections.map((section) => {
                                                return {
                                                    ...section,
                                                    subSectionsCount: section.subSections.length,
                                                    sectionTime: dayjs.utc().hour(section.sectionTime.split(':')[0]).minute(section.sectionTime.split(':')[1]).second(0).millisecond(0),
                                                }
                                            });
                                        }
                                        setConfiguration({
                                            ...configuration,
                                            ...dataToSet
                                        });
                                        setErrors({
                                            ...errors,
                                            examTimes: batches.map(() => ({
                                                startTime: null,
                                                endTime: null,
                                            })),
                                            sections: data.sectionWise
                                                ? dataToSet.sections.map((s) => ({
                                                    timeError: null,
                                                    titleError: null,
                                                    countError: null,
                                                    subSections: Array.from({ length: s.subSectionsCount }, () => null)
                                                }))
                                                : errors.sections,
                                            reminders: data.reminders.map(() => null)
                                        });
                                        setDocumentFiles({
                                            logTablesFile: null,
                                            signedLogTablesFile: data.logTables ?? null,
                                            removeLogTablesFile: false,
                                            pvChartsFile: null,
                                            signedPvChartsFile: data.pvCharts ?? null,
                                            removePvChartsFile: false,
                                            heatTableFile: null,
                                            signedHeatTableFile: data.heatTable ?? null,
                                            removeHeatTableFile: false,
                                            helpDocumentFile: null,
                                            signedHelpDocumentFile: data.helpDocument ?? null,
                                            removeHelpDocumentFile: false,
                                        });
                                        setConfigurationDetails(data);
                                    }
                                } catch (error) {
                                    console.log('There is an error while fetching content configuration details', error);
                                }
                            }
                            fetchDetails();
                        } else {
                            setConfiguration({
                                ...configuration,
                                examTimes: batches,
                            });
                            setErrors({
                                ...errors,
                                examTimes: batches.map(() => ({
                                    startTime: null,
                                    endTime: null,
                                }))
                            });
                        }
                    }
                } catch (error) {
                    console.log('There is an error while getting batches', error);
                }
            }
            getBatches();
        }
    }, [location, projectName, navigate]);

    useEffect(() => {
        const fetchViolations = async () => {
            try {
                const res = await getAllViolations();           
                const list = res?.data?.violations ?? [];
                if (res.code === 'OK' && Array.isArray(list) && list.length) {
                    setAvailableViolations(list.filter((v) => !v.isDeleted));
                } else {
                    setAvailableViolations(defaultViolations.filter((v) => !v.isDeleted));
                }
            } catch {
                setAvailableViolations(defaultViolations.filter((v) => !v.isDeleted));
            }
        };
        fetchViolations();
    }, []);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };
    const handleSubmitButtonDisplayMinutesChange = (value, err) => {
        let error = err.validationError ? 'Please enter valid time.' : null;
        if (!value) {
            error = 'Please enter valid time.'
        } else {
            if (value.hour() === 0 && value.minute() === 0) {
                error = 'Please enter valid time.'
                // } else if (configuration.sectionWise === 'Yes' && (configuration.sectionNavigation === 'MOVEMENT_RESTRICTION_WITH_INCREMENTAL_TIME' || configuration.sectionNavigation === 'FIXED_TIME_AND_MOVEMENT_RESTRICTION')) {
                //     let timeDurationInMinutes = 0;
                //     const sectionTime = configuration.sections[configuration.sections.length - 1]?.sectionTime;
                //     if (sectionTime) {
                //         timeDurationInMinutes = sectionTime.hour() * 60 + sectionTime.minute();
                //     }
                //     const submitButtonDisplayMinutesInMinutes = value.hour() * 60 + value.minute();
                //     if (timeDurationInMinutes < submitButtonDisplayMinutesInMinutes) {
                //         error = 'Submit button can only be visible in last section.'
                //     }
            } else {
                let timeDurationInMinutes = 0;
                if (configuration.timeDuration) {
                    timeDurationInMinutes = configuration.timeDuration.hour() * 60 + configuration.timeDuration.minute();
                }
                const submitButtonDisplayMinutesInMinutes = value.hour() * 60 + value.minute();
                if (timeDurationInMinutes < submitButtonDisplayMinutesInMinutes) {
                    error = 'value can not be greater than time duration.'
                }
            }
        }
        setErrors({
            ...errors,
            submitButtonDisplayMinutes: error,
        });
        if (value) {
            setConfiguration({
                ...configuration,
                submitButtonDisplayMinutes: value
            });
        }
    }
    const handleTimeDurationChange = (value, err) => {
        let error = err.validationError ? 'Please enter valid time.' : null;
        if (!value) {
            error = 'Please enter valid time.'
        }
        const updatableConfiguration = {}
        if (value) {
            updatableConfiguration.timeDuration = value;
            if (value.hour() === 0 && value.minute() === 0) {
                error = 'Please enter valid time.'
            } else {
                let timeDurationInMinutes = (value.hour() * 60) + value.minute();
                let timeDurationForScribeInMinutes = (configuration.timeDurationForScribe.hour() * 60) + configuration.timeDurationForScribe.minute();
                if (isNaN(timeDurationForScribeInMinutes)) {
                    timeDurationForScribeInMinutes = 0;
                }
                if (timeDurationForScribeInMinutes > timeDurationInMinutes) {
                    timeDurationInMinutes = timeDurationForScribeInMinutes;
                }
                updatableConfiguration.examTimes = configuration.examTimes.map((eTime) => {
                    if (eTime.startTime.diff(eTime.endTime, 'minute') != timeDurationInMinutes) {
                        eTime.endTime = dayjs(eTime.startTime).add(timeDurationInMinutes, 'minutes');
                    }
                    return eTime;
                });
            }
        }
        const scribeError = getScribeError(value, configuration.timeDurationForScribe);
        setErrors({
            ...errors,
            timeDuration: error,
            timeDurationForScribe: scribeError,
        });
        if (value) {
            setConfiguration({
                ...configuration,
                ...updatableConfiguration,
            });
        }
    }
    const handleTimeDurationForScribeChange = (value, err) => {
        let error = err.validationError ? 'Please enter valid time.' : null;
        if (!value) {
            error = 'Please enter valid time.'
        }
        const updatableConfiguration = {}
        if (value) {
            updatableConfiguration.timeDurationForScribe = value;
            if (value.hour() === 0 && value.minute() === 0) {
                error = 'Please enter valid time.'
            } else {
                let timeDurationForScribeInMinutes = (value.hour() * 60) + value.minute();
                let timeDurationInMinutes = (configuration.timeDuration.hour() * 60) + configuration.timeDuration.minute();
                if (isNaN(timeDurationInMinutes)) {
                    timeDurationInMinutes = 0;
                }
                if (timeDurationInMinutes > timeDurationForScribeInMinutes) {
                    timeDurationForScribeInMinutes = timeDurationInMinutes;
                }
                updatableConfiguration.examTimes = configuration.examTimes.map((eTime) => {
                    if (eTime.startTime.diff(eTime.endTime, 'minute') != timeDurationForScribeInMinutes) {
                        eTime.endTime = dayjs(eTime.startTime).add(timeDurationForScribeInMinutes, 'minutes');
                    }
                    return eTime;
                });
            }
        }

        const scribeError = getScribeError(configuration.timeDuration, value);
        setErrors({
            ...errors,
            timeDurationForScribe: scribeError ?? error,
        });
        if (value) {
            setConfiguration({
                ...configuration,
                ...updatableConfiguration
            });
        }
    }
    const handleStartExamBetweenSpecifiedTimeChange = (e) => {
        const value = e.target.checked;
        let timeDurationInMinutes = (configuration.timeDuration.hour() * 60) + configuration.timeDuration.minute();
        if (isNaN(timeDurationInMinutes)) {
            timeDurationInMinutes = 0;
        }
        let timeDurationForScribeInMinutes = (configuration.timeDurationForScribe.hour() * 60) + configuration.timeDurationForScribe.minute();
        if (isNaN(timeDurationForScribeInMinutes)) {
            timeDurationForScribeInMinutes = 0;
        }
        if (timeDurationForScribeInMinutes > timeDurationInMinutes) {
            timeDurationInMinutes = timeDurationForScribeInMinutes;
        }
        setConfiguration({
            ...configuration,
            startExamBetweenSpecifiedTime: value,
            examTimes: configuration.examTimes.map((examTime) => {
                if (examTime.startTime) {
                    examTime.endTime = dayjs(examTime.startTime).add(timeDurationInMinutes, 'minutes');
                }
                return examTime;
            }),
            lateLoginAllowed: 'No',
            lateLoginMinutes: dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
            forcedLogout: value ? 'No' : configuration.forcedLogout,
            loginBeforeMinutes: dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
        });
        setErrors({
            ...errors,
            lateLoginMinutes: null,
            examTimes: errors.examTimes.map((examTimeErr) => ({
                startTime: examTimeErr.startTime,
                endTime: null,
            })),
            loginBeforeMinutes: null,
        });
    }
    const handleExamStartTimeChange = (value, err, index) => {
        let error = err.validationError ? 'Please enter valid time.' : null;
        if (!value) {
            error = 'Please enter valid time.'
        }
        if (value && !canEditNow) {
            const currentTime = dayjs();
            const startTime = dayjs(configuration.examTimes[index].startTime);
            const localStartTime = dayjs().date(startTime.date()).month(startTime.month()).year(startTime.year()).hour(value.hour()).minute(value.minute()).second(0).millisecond(0);
            if (localStartTime.isBefore(currentTime)) {
                error = 'Start time can not be in past.';
            }
        }
        setErrors({
            ...errors,
            examTimes: errors.examTimes.map((examTimeErr, i) => {
                if (i === index) {
                    examTimeErr.startTime = error;
                }
                return examTimeErr;
            })
        });
        if (error) return;
        if (value) {
            let timeDurationInMinutes = (configuration.timeDuration.hour() * 60) + configuration.timeDuration.minute();
            if (isNaN(timeDurationInMinutes)) {
                timeDurationInMinutes = 0;
            }
            let timeDurationForScribeInMinutes = (configuration.timeDurationForScribe.hour() * 60) + configuration.timeDurationForScribe.minute();
            if (isNaN(timeDurationForScribeInMinutes)) {
                timeDurationForScribeInMinutes = 0;
            }
            if (timeDurationForScribeInMinutes > timeDurationInMinutes) {
                timeDurationInMinutes = timeDurationForScribeInMinutes;
            }
            setConfiguration({
                ...configuration,
                examTimes: configuration.examTimes.map((examTime, i) => {
                    if (i === index) {
                        examTime.startTime = value;
                        if (!configuration.startExamBetweenSpecifiedTime) {
                            examTime.endTime = dayjs(value).add(timeDurationInMinutes, 'minutes');
                        } else if (examTime.endTime.diff(value, 'minute') < timeDurationInMinutes) {
                            examTime.endTime = dayjs(value).add(timeDurationInMinutes, 'minutes');
                        }
                    }
                    return examTime;
                })
            });
            setErrors({
                ...errors,
                examTimes: errors.examTimes.map((examTimeErr, i) => {
                    if (i === index) {
                        examTimeErr.endTime = null;
                    }
                    return examTimeErr;
                })
            });
        }
    }
    const handleExamEndDateChange = (index, value, err) => {
        let error = err.validationError ? 'Please enter valid end date.' : null;
        if (!value) {
            error = 'Please enter valid end date.';
        }
        setErrors({
            ...errors,
            examTimes: errors.examTimes.map((examTimeErr, i) => {
                if (i === index) {
                    examTimeErr.endTime = error;
                }
                return examTimeErr;
            })
        });
        setConfiguration({
            ...configuration,
            examTimes: configuration.examTimes.map((examTime, i) => {
                if (i === index) {
                    examTime.endTime = value;
                }
                return examTime;
            })
        });
    }
    const handleExamStartDateChange = (index, value, err) => {
        let error = err.validationError ? 'Please enter valid start date.' : null;
        if (!value) {
            error = 'Please enter valid start date.';
        }
        setErrors({
            ...errors,
            examTimes: errors.examTimes.map((examTimeErr, i) => {
                if (i === index) {
                    examTimeErr.startTime = error;
                }
                return examTimeErr;
            })
        });
        if (error) return;
        const existingStartTime = configuration.examTimes[index].startTime;
        const newStartTime = value.hour(existingStartTime.hour()).minute(existingStartTime.minute()).second(0).millisecond(0);
        let timeDurationInMinutes = (configuration.timeDuration.hour() * 60) + configuration.timeDuration.minute();
        if (isNaN(timeDurationInMinutes)) timeDurationInMinutes = 0;
        let timeDurationForScribeInMinutes = (configuration.timeDurationForScribe.hour() * 60) + configuration.timeDurationForScribe.minute();
        if (isNaN(timeDurationForScribeInMinutes)) timeDurationForScribeInMinutes = 0;
        if (timeDurationForScribeInMinutes > timeDurationInMinutes) {
            timeDurationInMinutes = timeDurationForScribeInMinutes;
        }
        setConfiguration({
            ...configuration,
            examTimes: configuration.examTimes.map((examTime, i) => {
                if (i === index) {
                    examTime.startTime = newStartTime;
                    if (!configuration.startExamBetweenSpecifiedTime) {
                        examTime.endTime = dayjs(newStartTime).add(timeDurationInMinutes, 'minutes');
                    }
                }
                return examTime;
            })
        });
    }

    const handleExamEndTimeChange = (value, err, index) => {
        let error = err.validationError ? 'Please enter valid time.' : null;
        if (!value) {
            error = 'Please enter valid end time.'
        }
        // if (value && value.hour() === 0 && value.minute() === 0) {
        //     error = 'Please enter valid end time.'
        // }
        if (!error && value) {
            const startTime = configuration.examTimes[index].startTime;
            if (startTime && startTime.isSameOrAfter(value)) {
                error = 'End time must be after start time.';
            }
        }
        if (!error && value) {
            const hours = configuration.timeDurationForScribe.hour();
            const minutes = configuration.timeDurationForScribe.minute();
            const timeDurationInMinutes = (hours * 60) + minutes;
            const startTime = value.clone().subtract(timeDurationInMinutes, 'minutes');

            const currentStartTime = configuration.examTimes[index].startTime;
            if (currentStartTime && currentStartTime.isAfter(startTime)) {
                error = 'Duration between start time and end time must have at least exam duration.';
            }
        }
        setErrors({
            ...errors,
            examTimes: errors.examTimes.map((examTimeErr, i) => {
                if (i === index) {
                    examTimeErr.endTime = error;
                }
                return examTimeErr;
            })
        });
        if (error) return;
        if (value) {
            setConfiguration({
                ...configuration,
                examTimes: configuration.examTimes.map((examTime, i) => {
                    if (i === index) {
                        examTime.endTime = value;
                    }
                    return examTime;
                })
            });
        }
    }
    const handleLoginBeforeMinutesChange = (value, err) => {
        let error = err.validationError ? 'Please enter valid time.' : null;
        if (!value) {
            error = 'Please enter valid time.'
        }
        // if (value && value.hour() === 0 && value.minute() === 0) {
        //     error = 'Please enter valid time.'
        // }
        setErrors({
            ...errors,
            loginBeforeMinutes: error,
        });
        if (value) {
            setConfiguration({
                ...configuration,
                loginBeforeMinutes: value
            });
        }
    }

    const handleLateLoginMinuteChange = (value, err) => {
        let error = err.validationError ? 'Please enter valid time.' : null;
        if (!value) {
            error = 'Please enter valid time.';
        } else {
            if (value.hour() === 0 && value.minute() === 0) {
                error = 'Please enter valid time.'
            } else {
                let timeDurationInMinutes = 0;
                if (configuration.timeDuration) {
                    timeDurationInMinutes = configuration.timeDuration.hour() * 60 + configuration.timeDuration.minute();
                }
                const lateLoginMinutesInMinutes = value.hour() * 60 + value.minute();
                if (timeDurationInMinutes < lateLoginMinutesInMinutes) {
                    error = 'value can not be greater than time duration.'
                }
            }
        }
        setErrors({
            ...errors,
            lateLoginMinutes: error,
        });
        if (value) {
            setConfiguration({
                ...configuration,
                lateLoginMinutes: value
            });
        }
    }

    const handleReminderCountChange = (e) => {
        let count = e.target.value;
        let error = null;
        if (!count || Number(count) < 0) {
            error = count === 0 ? null : 'Please enter valid count.';
        } else if (Number(count) > 10) {
            error = 'Reminders can not be more than 10.'
        }

        setErrors({
            ...errors,
            remindersCount: error
        });
        setRemindersCount(count);
    }
    const handleReminderInputCheck = () => {
        const currentReminders = configuration.reminders.length;
        let reminderErrors = errors.reminders;
        if (currentReminders !== remindersCount) {
            let newReminders = configuration.reminders;
            if (currentReminders > remindersCount) {
                newReminders = newReminders.slice(0, remindersCount);
                reminderErrors = reminderErrors.slice(0, remindersCount);
            } else {
                for (let i = currentReminders; i < remindersCount; i++) {
                    newReminders.push(dayjs.utc().hour(0).minute(0).second(0).millisecond(0))
                    reminderErrors.push(null);
                }
            }
            setConfiguration({
                ...configuration,
                reminders: newReminders
            });
            setErrors({
                ...errors,
                reminders: reminderErrors
            });
        }
    }
    const handleRemindersTimeChange = (value, err, index) => {
        let error = err.validationError ? 'Please enter valid time.' : null;
        if (!value) {
            error = 'Please enter valid time.'
        }
        if (value) {
            const timeDuration = configuration.timeDuration.hour() * 60 + configuration.timeDuration.minute();
            const reminderTime = value.hour() * 60 + value.minute();
            if (value.hour() === 0 && value.minute() === 0) {
                error = 'Please enter valid time.'
            } else if (timeDuration < reminderTime) {
                error = 'Reminder time cannot be more than exam time.'
            } else {
                for (let i = 0; i < configuration.reminders.length; i++) {
                    if (i !== index && value.hour() == configuration.reminders[i].hour() && value.minute() === configuration.reminders[i].minute()) {
                        error = 'Reminder time must be unique.';
                    }
                }
            }
        }

        if (error) {
            const reminderErrors = errors.reminders.map((rErr, i) => {
                if (index === i) {
                    return error;
                }
                return rErr;
            });
            setErrors({
                ...errors,
                reminders: reminderErrors,
            });
            return;
        };

        if (value) {
            const newReminders = configuration.reminders.map((reminder, i) => {
                if (index === i) {
                    return value;
                }
                return reminder;
            });
            const reminderErrors = errors.reminders.map((rErr, i) => {
                if (index === i) {
                    return null;
                }
                return rErr;
            })
            setErrors({
                ...errors,
                reminders: reminderErrors
            });
            setConfiguration({
                ...configuration,
                reminders: newReminders
            });
        }
    }
    const getScribeError = (duration, scribeDuration) => {
        if (duration === null || scribeDuration === null) {
            return null
        };
        if (scribeDuration.isBefore(duration)) {
            return 'Scribe candidate must have at least same duration as regular candidate.';
        }
        return null;
    }
    // const getEndTimeErrors = (duration, startTime, endTime) => {
    //     if (duration === null || startTime === null || endTime === null) {
    //         return null
    //     };
    //     const minutes = (duration.hour() * 60) + duration.minute();
    //     if (!minutes) {
    //         return null;
    //     };
    //     const calculatedEndTime = dayjs(startTime).add(minutes, 'minutes');
    //     if ((calculatedEndTime.hour() !== endTime.hour()) || (calculatedEndTime.minute() !== endTime.minute())) {
    //         return 'Time duration does not match with exam start time and exam end time.'
    //     }
    //     return null;
    // }
    const handleExamTypeChange = (e) => {
        setConfiguration({
            ...configuration,
            examType: e.target.value,
            // MEDIUM/HIGH security exist only for MCQ exams
            ...(e.target.value !== 'MCQ' && ['MEDIUM', 'HIGH'].includes(configuration.securityLevel) && { securityLevel: 'NONE', dataEncryptionKeyScope: null }),
            sectionWise: 'No',
            sectionCount: 1,
            sections: [{
                sectionTitle: 'Section 1',
                noOfQuestions: 0,
                sectionTime: dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
                subSectionsCount: 0,
                subSections: []
            }],
            calculator: 'No',
            calculatorType: 'normal',
            contentUploadMethod: 'set',
            numberOfEasyQuestions: 0,
            numberOfMediumQuestions: 0,
            numberOfHardQuestions: 0,
            numberOfSets: 1,
            questionsPerSet: 1,
            numberOfAnswerPages: 1,
            feedback: 'No',
            numberOfFeedbackQuestions: 1,
            maximumLoginAttempts: 'No',
            maximumLoginAttemptsCount: 1,
            allQuestionsMandatory: 'No',
            questionNavigationForwardOnly: 'No',
            negativeMarking: 'No',
            instantScore: 'No',
            instantResult: 'No',
            passingMarks: null,
        });
        setSectionCount(1);
        setErrors({
            ...errors,
            sections: [{
                timeError: null,
                titleError: null,
                countError: null,
                subSections: []
            }],
            sectionError: null,
            numberOfSets: null,
            numberOfQuestions: null,
            numberOfFeedbackQuestions: null,
            maximumLoginAttemptsCount: null,
            numberOfAnswerPages: null,
            lowQuestions: null,
        });
        setDocumentFiles({
            logTablesFile: null,
            signedLogTablesFile: null,
            removeLogTablesFile: false,
            pvChartsFile: null,
            signedPvChartsFile: null,
            removePvChartsFile: false,
            heatTableFile: null,
            signedHeatTableFile: null,
            removeHeatTableFile: false,
            helpDocumentFile: null,
            signedHelpDocumentFile: null,
            removeHelpDocumentFile: false,
        });
    }
    const handleSectionNavigationChange = (e) => {
        const value = e.target.value;
        let sections = configuration.sections;
        let needToSetErrors = false;
        if (value === 'NO_MOVEMENT_OR_TIME_RESTRICTION' || value === 'MOVEMENT_RESTRICTION_WITH_NO_TIME') {
            sections = sections.map((section) => {
                section.sectionTime = section.sectionTime.hour(0).minute(0).second(0).millisecond(0);
                return section;
            });
            needToSetErrors = true;
        }
        setConfiguration({
            ...configuration,
            sections: sections,
            sectionNavigation: e.target.value,
        });
        if (needToSetErrors) {
            setErrors({
                ...errors,
                sections: errors.sections.map((sectionErr) => ({
                    ...sectionErr,
                    timeError: null,
                }))
            });
        }
    }
    const handleSectionWiseChange = (value) => {
        if (value === 'No') {
            setErrors({
                ...errors,
                sectionError: null,
                sections: [{
                    timeError: null,
                    titleError: null,
                    countError: null,
                    subSections: []
                }],
                lowQuestions: null,
            });
        }
        setSectionCount(1);
        setConfiguration({
            ...configuration,
            sectionWise: value,
            sectionCount: 1,
            contentUploadMethod: value === 'No' ? configuration.contentUploadMethod : 'set',
            sections: [{
                sectionTitle: 'Section 1',
                noOfQuestions: 0,
                sectionTime: dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
                subSectionsCount: 0,
                subSections: []
            }]
        });
    }

    const handleSelectionTypeChange = (value) => {
        setConfiguration((prev) => {
            return {
                ...prev,
                selectionType: value,
                randomizeQuestions: value === 'random' ? 'Yes' : prev.randomizeQuestions,
            };
        });
    };

    const handleSectionInputCheck = () => {
        let sections = configuration.sections;
        let sectionsErrors = errors.sections;
        if (configuration.sectionCount !== sectionCount) {
            if (sectionCount > configuration.sectionCount) {
                for (let z = configuration.sectionCount + 1; z <= sectionCount; z++) {
                    sections.push({
                        sectionTitle: `Section ${z}`,
                        sectionTime: dayjs.utc().hour(0).minute(0).second(0).millisecond(0),
                        subSectionsCount: 0,
                        subSections: []
                    })
                    sectionsErrors.push({
                        timeError: null,
                        titleError: null,
                        countError: null,
                        subSections: []
                    });
                }
                setErrors({
                    ...errors,
                    sections: sectionsErrors
                })
            } else {
                sections.splice(sectionCount, configuration.sectionCount - sectionCount);
                sectionsErrors.splice(sectionCount, configuration.sectionCount - sectionCount);
                setErrors({
                    ...errors,
                    sections: sectionsErrors
                });
            }
        }
        setConfiguration({
            ...configuration,
            sectionCount: sectionCount,
            sections: sections,
        });
    }
    const handleSectionTitleChange = (event, index) => {
        const value = event.target.value;
        let error = null;
        if (!value) {
            error = 'Please enter valid section title.';
        }

        const newSections = configuration.sections.map((section, i) => {
            if (index === i) {
                section.sectionTitle = value;
            }
            return section;
        });
        const sectionErrors = errors.sections.map((sectionErr, i) => {
            if (index === i) {
                return {
                    ...sectionErr,
                    titleError: error
                }
            }
            return sectionErr;
        });
        setConfiguration({
            ...configuration,
            sections: newSections
        });
        setErrors({
            ...errors,
            sections: sectionErrors
        });
    }

    const handleSectionNoOfQuestionsChange = (event, index) => {
        const value = event.target.value;
        let error = null;
        if (value === '') {
            error = 'Please enter number of questions';
        } else if (!/^[1-9][0-9]*$/.test(value)) {
            error = 'Value must be greater than 0';
        }

        const newSections = configuration.sections.map((section, i) => {
            if (index === i) {
                section.noOfQuestions = value;
            }
            return section;
        });

        const sectionError = getSectionQuestionsError(newSections);
        const qpsError = getQuestionsPerSetMismatchError(newSections, configuration.questionsPerSet);
        setConfiguration({
            ...configuration,
            sections: newSections
        });
        setErrors({
            ...errors,
            sectionError: sectionError || qpsError,
            sections: errors.sections.map((err, i) =>
                i === index ? { ...err, titleError: error } : err
            )
        });
    }

    const handleSectionTimeChange = (value, err, index) => {
        let error = err.validationError ? 'Please enter valid time.' : null;
        if (!value) {
            error = 'Please enter valid time.'
        }
        if (value && value.hour() === 0 && value.minute() === 0) {
            error = 'Please enter valid time.'
        }


        if (error) {
            const sectionErrors = errors.sections.map((sectionErr, i) => {
                if (index === i) {
                    return {
                        ...sectionErr,
                        timeError: error
                    };
                }
                return sectionErr;
            });
            setErrors({
                ...errors,
                sections: sectionErrors,
                sectionError: null,
            });
            return;
        };

        if (value) {
            const newSections = configuration.sections.map((section, i) => {
                if (index === i) {
                    section.sectionTime = value;
                }
                return section;
            });
            const sectionError = getSectionsTimeError(newSections, configuration.timeDuration);
            if (typeof sectionError === 'number') {
                let sectionsErrors = errors.sections.map((sectionErr, i) => {
                    if (i === sectionError) {
                        return {
                            ...sectionErr,
                            timeError: 'Please enter valid time.'
                        }
                    }
                    if (i === index) {
                        return {
                            ...sectionErr,
                            timeError: null
                        }
                    }
                    return sectionErr;
                });
                setErrors({
                    ...errors,
                    sections: sectionsErrors,
                });
            } else {
                setErrors({
                    ...errors,
                    sections: errors.sections.map(() => ({
                        timeError: null,
                        titleError: null,
                        countError: null,
                        subSections: []
                    })),
                    sectionError: sectionError
                });
            }
            setConfiguration({
                ...configuration,
                sections: newSections
            });
        }
    }
    const getSectionsTimeError = (sections, duration) => {
        if (duration === null || configuration.sectionNavigation === 'NO_MOVEMENT_OR_TIME_RESTRICTION' || configuration.sectionNavigation === 'MOVEMENT_RESTRICTION_WITH_NO_TIME') return null;
        for (let z = 0; z < sections.length; z++) {
            let section = sections[z];
            if (section.sectionTime.hour() === 0 && section.sectionTime.minute() === 0) {
                return z;
            }
        }

        const totalMinutes = (duration.hour() * 60) + duration.minute();
        let minutes = 0;
        for (let section of sections) {
            minutes += (section.sectionTime.hour() * 60) + section.sectionTime.minute();
        }
        if (totalMinutes !== minutes) {
            return 'Total time for sections must match with exam time duration.';
        }
        return null;
    }
    const handleNumberOfFeedbackQuestionsChange = (e) => {
        let count = e.target.value;
        if (count) {
            if (Number(count) < 0) {
                count = 0;
            } else if (Number(count) > 100) {
                count = 100;
            }
        }
        setErrors({
            ...errors,
            numberOfFeedbackQuestions: !count || Number(count) === 0 ? 'Please enter valid number.' : null
        });
        setConfiguration({
            ...configuration,
            numberOfFeedbackQuestions: count
        });
    }
    const handleCountChange = (e) => {
        let count = e.target.value;
        if (count) {
            if (Number(count) < 0) {
                count = 0;
            } else if (Number(count) > 20) {
                count = 20;
            }
        }
        setErrors({
            ...errors,
            sectionCount: !count || Number(count) === 0 ? 'Please enter valid number' : null,
        });
        setSectionCount(count);
    }
    const handleContentUploadMethodChange = (e, value) => {
        setConfiguration({
            ...configuration,
            numberOfEasyQuestions: 0,
            numberOfMediumQuestions: 0,
            numberOfHardQuestions: 0,
            contentUploadMethod: value,
        });
        setErrors({
            ...errors,
            numberOfEasyQuestions: null,
            numberOfMediumQuestions: null,
            numberOfHardQuestions: null,
            numberOfQuestions: null,
        })
    }
    const handleNumberOfSetsChange = (e) => {
        let value = e.target.value;
        if (value) {
            if (Number(value) < 0) {
                value = 0;
            } else if (Number(value) > 100) {
                value = 100;
            }
        }
        if (!value || Number(value) === 0) {
            setErrors({
                ...errors,
                numberOfSets: 'Please enter valid value',
            })
        } else if (Number(value) < configuration.examTimes.length) {
            setErrors({
                ...errors,
                numberOfSets: 'Number of sets must be same or more than available batches.'
            });
        } else {
            setErrors({
                ...errors,
                numberOfSets: null
            });
        }
        setConfiguration({
            ...configuration,
            numberOfSets: value,
        });
    }
    const handleNumberOfQuestionsChange = (e, field) => {
        let value = e.target.value;
        let fieldError = null;
        if (!value || value < 0 || isNaN(value)) {
            fieldError = 'Please enter valid value.';
        }
        const error = getNumberOfQuestionsError(!value ? 0 : value, field);

        setErrors({
            ...errors,
            [field]: fieldError,
            numberOfQuestions: error
        });
        setConfiguration({
            ...configuration,
            [field]: value,
        });
    }
    const getNumberOfQuestionsError = (value, field) => {
        let totalQuestions = 0;
        let error = null;
        if (field === 'questionsPerSet') {
            totalQuestions = Number(configuration.numberOfEasyQuestions) + Number(configuration.numberOfMediumQuestions) + Number(configuration.numberOfHardQuestions);
        } else if (field === 'numberOfEasyQuestions') {
            totalQuestions = Number(value) + Number(configuration.numberOfMediumQuestions) + Number(configuration.numberOfHardQuestions);
        } else if (field === 'numberOfMediumQuestions') {
            totalQuestions = Number(configuration.numberOfEasyQuestions) + Number(value) + Number(configuration.numberOfHardQuestions);
        } else {
            totalQuestions = Number(configuration.numberOfEasyQuestions) + Number(configuration.numberOfMediumQuestions) + Number(value);
        }
        const questionsPerSet = field === 'questionsPerSet' ? Number(value) : Number(configuration.questionsPerSet);
        if (questionsPerSet === 0 || Number(questionsPerSet) === 0) {
            error = 'Please enter valid value';
        } else if (questionsPerSet !== totalQuestions) {
            error = 'Number of questions must be same as questions per set.';
        }
        return error;
    }
    const getLowQuestionsError = (value) => {
        if (configuration.sectionWise === 'Yes') {
            if (value < configuration.sections.length) {
                return 'Number of questions must be more than number of sections';
            }
        }
        return null;
    }
    const handleQuestionsPerSetChange = (e) => {
        let value = e.target.value;
        if (value) {
            if (Number(value) < 0) {
                value = 0;
            } else if (Number(value) > 500) {
                value = 500;
            }
        }
        const numberOfQuestionsError = getNumberOfQuestionsError(value ? value : 0, 'questionsPerSet')
        const lowQuestionsError = getLowQuestionsError(value);
        let sectionError = null;
        if (configuration.sectionWise === 'Yes' && configuration.sections.length > 0) {
            sectionError = getQuestionsPerSetMismatchError(configuration.sections, value);
        }
        setErrors({
            ...errors,
            numberOfQuestions: numberOfQuestionsError,
            lowQuestions: lowQuestionsError,
            sectionError: sectionError,
        })
        setConfiguration({
            ...configuration,
            questionsPerSet: value,
        });
    }
    const handleMaxMarksChange = (e) => {
        let value = e.target.value;
        if (value) {
            if (Number(value) < 0) {
                value = 0;
            } else if (Number(value) > 1000) {
                value = 1000;
            }
        }
        setErrors({
            ...errors,
            maxMarks: !value || Number(value) === 0 ? 'Please enter valid value' : null
        });
        setConfiguration({
            ...configuration,
            maxMarks: value,
        });
    }
    const handleMaximumLoginAttemptsCountChange = (e) => {
        let count = e.target.value;
        if (count) {
            if (Number(count) < 0) {
                count = 0;
            } else if (Number(count) > 100) {
                count = 100;
            }
        }
        setErrors({
            ...errors,
            maximumLoginAttemptsCount: !count || Number(count) === 0 ? 'Please enter valid number.' : null
        });
        setConfiguration({
            ...configuration,
            maximumLoginAttemptsCount: count,
        });
    }
    const handleMaximumLoginAttemptsChange = (value) => {
        setConfiguration({
            ...configuration,
            maximumLoginAttempts: value,
            maximumLoginAttemptsCount: value === 'No' ? 1 : configuration.maximumLoginAttemptsCount,
        });
    }
    const handleConfigChange = (field, value) => {
        if (field === 'autoLogout') {
            setConfiguration({
                ...configuration,
                [field]: value,
            });
            setErrors({
                ...errors,
                submitButtonDisplayMinutes: null,
            })
        } else if (field === 'feedback') {
            setConfiguration({
                ...configuration,
                numberOfFeedbackQuestions: value === 'Yes' ? 1 : configuration.numberOfFeedbackQuestions,
                [field]: value,
            });
            setErrors({
                ...errors,
                numberOfFeedbackQuestions: null,
            })
        } else if (field === 'biometricRequired') {
            setConfiguration({
                ...configuration,
                biometricRequired: value,
                biometricMethod: [],
            });
            setBiometricMethodError(null);
        }
        else if (field === 'instantResult') {
            setConfiguration({
                ...configuration,
                instantResult: value,
                passingMarks: value === 'Yes' ? configuration.passingMarks : null,
            });
            setErrors({
                ...errors,
                passingMarks: null,
            })
        }
        else {
            setConfiguration({
                ...configuration,
                [field]: value,
            });
        }
    }
    const handleNumberOfAnswerPagesChange = (e) => {
        let count = e.target.value;
        if (count) {
            if (Number(count) < 0) {
                count = 0;
            } else if (Number(count) > 100) {
                count = 100;
            }
        }
        setErrors({
            ...errors,
            numberOfAnswerPages: !count || Number(count) === 0 ? 'Please enter valid number' : null,
        });
        setConfiguration({
            ...configuration,
            numberOfAnswerPages: count,
        });
    }
    const handleSelectedViolationsChange = (e) => {
        const newSelected = e.target.value;
        const updatedTrigger = configuration.triggerViolations.filter((wc) => newSelected.includes(wc));
        setViolationErrors((prev) => ({ ...prev, selectedViolations: newSelected.length ? null : prev.selectedViolations }));
        setConfiguration({
            ...configuration,
            selectedViolations: newSelected,
            triggerViolations: updatedTrigger,
        });
    };

    const handleTriggerViolationsChange = (e) => {
        const newTrigger = e.target.value;
        if (newTrigger.length) {
            setViolationErrors((prev) => ({ ...prev, triggerViolations: null }));
        }
        setConfiguration({
            ...configuration,
            triggerViolations: newTrigger,
        });
    };

    const handleViolationAutoSubmitChange = (e, value) => {
        setConfiguration({
            ...configuration,
            isViolationAutoSubmitEnabled: value,
            ...(value === 'No' && { violationThresholdCount: 0, triggerViolations: [] }),
        });
        if (value === 'No') {
            setViolationErrors({ selectedViolations: null, violationThresholdCount: null });
        }
    };

    const handleAiAlertBeepChange = (e, value) => {
        setConfiguration({
            ...configuration,
            aiAlertBeepEnabled: value,
        });
    };

    const handleViolationThresholdCountChange = (e) => {
        const val = e.target.value;
        setViolationErrors((prev) => ({
            ...prev,
            violationThresholdCount: !val || Number(val) <= 0 ? 'Count must be greater than zero' : null,
        }));
        setConfiguration({ ...configuration, violationThresholdCount: val });
    };

    const handleIsBilingualChange = (e, value) => {
        setConfiguration({
            ...configuration,
            isBilingual: value,
            languages: [],
        });
    }
    const handleBilingualLanguagesChange = (e) => {
        if (e.target.value.length < 2) {
            setLanguagesError(true);
        } else {
            setLanguagesError(null);
        }
        setConfiguration({
            ...configuration,
            languages: e.target.value
        })
    }
    const handleBilingualLanguageChange = (e) => {
        if (!e.target.value) {
            setLanguagesError(true);
        } else {
            setLanguagesError(null);
        }
        setConfiguration({
            ...configuration,
            languages: [e.target.value]
        });
    }
    const handleBiometricMethodChange = (e) => {
        const value = e?.target?.value ?? [];
        if (value.length) {
            setBiometricMethodError(null);
        } else {
            setBiometricMethodError(true);
        }
        setConfiguration({
            ...configuration,
            biometricMethod: value
        });
    }

    const getQuestionsPerSetMismatchError = (sections, questionsPerSet) => {
        const qps = Number(questionsPerSet);

        if (!qps || qps <= 0) {
            return 'Questions per set must be greater than 0';
        }

        let totalQuestions = 0;

        for (let section of sections) {
            if (section.subSections && section.subSections.length > 0) {
                // If section has subsections, validate subsection total matches section total
                const subTotal = section.subSections.reduce(
                    (sum, ss) => sum + Number(ss.noOfQuestions || 0),
                    0
                );
                const sectionTotal = Number(section.noOfQuestions || 0);

                if (subTotal !== sectionTotal) {
                    return `Section "${section.sectionTitle}" total (${sectionTotal}) must match subsections total (${subTotal})`;
                }
                totalQuestions += subTotal;
            } else {
                totalQuestions += Number(section.noOfQuestions || 0);
            }
        }

        if (totalQuestions !== qps) {
            return `Questions per set (${qps}) must match total questions of all sections (${totalQuestions})`;
        }

        return null;
    };


    const executeLockedSave = async () => {
        setIsSaving(true);
        const body = {
            examTimes: configuration.examTimes.map((examTime) => ({
                projectMasterId: examTime.projectMasterId,
                batchId: examTime.batchId,
                startTime: examTime.startTime.format(),
                endTime: examTime.endTime.format(),
            })),
            maximumLoginAttempts: (configuration.examType !== 'DESCRIPTIVE' && configuration.maximumLoginAttempts === 'Yes') ? configuration.maximumLoginAttemptsCount : null,
            loginBeforeMinutes: configuration.loginBeforeMinutes.format('HH:mm'),
            lateLoginAllowed: !configuration.startExamBetweenSpecifiedTime && configuration.lateLoginAllowed === 'Yes',
            lateLoginMinutes: !configuration.startExamBetweenSpecifiedTime && configuration.lateLoginAllowed === 'Yes'
                ? (configuration.lateLoginMinutes.hour() * 60) + configuration.lateLoginMinutes.minute()
                : 0,
        };
        try {
            const res = await updateLockedExamConfig(configurationDetails.id, body);
            if (res.code === 'OK') {
                addNotification('Configuration updated successfully.', 'success');
                goToMasters();
            }
        } catch (error) {
            addNotification('Failed to update configuration.', 'danger');
            console.log('There is an error while updating locked configuration', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleConfirmLockedSave = async () => {
        setLockedSaveConfirmVisible(false);
        await executeLockedSave();
    };

    const handleSubmitForm = async () => {
        if (!drive?.id || !course?.id) return;

        if (isExamEnabled) {
            const examTimeErrors = [];
            let examTimeHasError = false;
            for (let i = 0; i < configuration.examTimes.length; i++) {
                const examTime = configuration.examTimes[i];
                const error = { startTime: null, endTime: null };
                const now = dayjs();
                if (configuration.startExamBetweenSpecifiedTime) {
                    const startTime = examTime.startTime;
                    const endTime = examTime.endTime;
                    const updatedEndTime = dayjs(endTime).subtract(configuration.timeDurationForScribe.hour(), 'hours').subtract(configuration.timeDurationForScribe.minute(), 'minutes');
                    if (endTime.isBefore(now)) {
                        error.endTime = 'End time cannot be in the past.';
                        examTimeHasError = true;
                    }
                    if (startTime.isSameOrAfter(endTime)) {
                        error.endTime = 'End time must be after start time.';
                        examTimeHasError = true;
                    } else if (updatedEndTime.isBefore(startTime)) {
                        error.endTime = 'Duration between start time and end time must have at least exam duration.';
                        examTimeHasError = true;
                    }
                }
                examTimeErrors.push(error);
            }
            if (examTimeHasError) {
                setErrors({ ...errors, examTimes: examTimeErrors });
                return;
            }
            if (configuration.maximumLoginAttempts === 'Yes') {
                const count = Number(configuration.maximumLoginAttemptsCount);
                if (isNaN(count) || !count || count === 0) {
                    setErrors({ ...errors, maximumLoginAttemptsCount: 'Please enter valid number' });
                    return;
                }
            }
            if (configuration.lateLoginAllowed === 'Yes') {
                let lateError = null;
                if (configuration.lateLoginMinutes.hour() === 0 && configuration.lateLoginMinutes.minute() === 0) {
                    lateError = 'Please enter valid time.';
                } else {
                    const timeDurationInMinutes = configuration.timeDuration.hour() * 60 + configuration.timeDuration.minute();
                    const lateLoginMinutesInMinutes = configuration.lateLoginMinutes.hour() * 60 + configuration.lateLoginMinutes.minute();
                    if (timeDurationInMinutes < lateLoginMinutesInMinutes) {
                        lateError = 'value can not be greater than time duration.';
                    }
                }
                if (lateError) {
                    setErrors({ ...errors, lateLoginMinutes: lateError });
                    return;
                }
            }
            setLockedSaveConfirmVisible(true);
            return;
        }
        
        if (configuration.proctoringEnabled === 'Yes' && !configuration.proctoringType) {
            addNotification('Please select proctoring type.', 'danger');
            return;
        }
        if (configuration.proctoringEnabled === 'Yes' && !configuration.proctoringEngine) {
            addNotification('Please select proctoring engine.', 'danger');
            return;
        }

        if (configuration.proctoringEnabled === 'Yes') {
            const newViolationErrors = { selectedViolations: null, violationThresholdCount: null, triggerViolations: null };
            let hasViolationError = false;
            if (!configuration.selectedViolations.length) {
                newViolationErrors.selectedViolations = 'At least one violation must be selected';
                hasViolationError = true;
            }
            if (configuration.isViolationAutoSubmitEnabled === 'Yes') {
                const thresholdCount = Number(configuration.violationThresholdCount);
                if (!configuration.violationThresholdCount || isNaN(thresholdCount) || thresholdCount <= 0) {
                    newViolationErrors.violationThresholdCount = 'Count must be greater than zero';
                    hasViolationError = true;
                }
                if (configuration.selectedViolations.length && !configuration.triggerViolations.length) {
                    newViolationErrors.triggerViolations = 'At least one trigger violation must be selected';
                    hasViolationError = true;
                }
            }
            if (hasViolationError) {
                setViolationErrors(newViolationErrors);
                return;
            }
        }

        if (configuration.isSafeExamBrowser && configuration.isSecureFolder && configuration.folderType === 'local') {
            const electronErrors = {};
            if (!configuration.whitelistedFolderPathWindows?.trim()) {
                electronErrors.whitelistedFolderPathWindows = 'Whitelisted folder path for Windows is required.';
            }
            if (!configuration.whitelistedFolderPathMac?.trim()) {
                electronErrors.whitelistedFolderPathMac = 'Whitelisted folder path for Mac is required.';
            }
            if (Object.keys(electronErrors).length) {
                setErrors({ ...errors, ...electronErrors });
                return;
            }
        }

        if (errors.timeDuration || errors.numberOfSets) return;
        if (configuration.examType !== 'TYPING' && errors.timeDurationForScribe) return;
        if (configuration.timeDuration.hour() === 0 && configuration.timeDuration.minute() === 0) {
            setErrors({
                ...errors,
                timeDuration: 'Please enter valid time.'
            });
            return;
        }

        for (let examTimeErr of errors.examTimes) {
            if (examTimeErr.startTime || examTimeErr.endTime) return;
        }
        const examTimeErrors = [];
        let examTimeHasError = false;
        for (let examTime of configuration.examTimes) {
            const error = {
                startTime: null,
                endTime: null
            };
            const currentTime = dayjs();
            const timezoneAttachedStartTime = dayjs(examTime.startTime);
            const localStartTime = dayjs().date(timezoneAttachedStartTime.date()).month(timezoneAttachedStartTime.month()).year(timezoneAttachedStartTime.year()).hour(examTime.startTime.hour()).minute(examTime.startTime.minute()).second(0).millisecond(0);
            if (!canEditNow && localStartTime.isBefore(currentTime)) {
                error.startTime = 'Start time can not be in past.';
                examTimeHasError = true;
            }
            // if (examTime.startTime.hour() === 0 && examTime.startTime.minute() === 0) {
            //     error.startTime = 'Please enter valid time.';
            //     examTimeHasError = true;
            // }
            if (configuration.startExamBetweenSpecifiedTime) {
                const startTime = examTime.startTime;
                const endTime = examTime.endTime;
                const updatedEndTime = dayjs(endTime).subtract(configuration.timeDurationForScribe.hour(), 'hours').subtract(configuration.timeDurationForScribe.minute(), 'minutes');
                // if (examTime.endTime.hour() === 0 && examTime.endTime.minute() === 0) {
                //     error.endTime = 'Please enter valid time.';
                //     examTimeHasError = true;
                // } else 
                if (startTime.isSameOrAfter(endTime)) {
                    error.endTime = 'End time must be after start time.';
                    examTimeHasError = true;
                } else if (updatedEndTime.isBefore(startTime)) {
                    error.endTime = 'Duration between start time and end time must have at least exam duration.';
                    examTimeHasError = true;
                }
            }
            examTimeErrors.push(error);
        }
        if (examTimeHasError) {
            setErrors({
                ...errors,
                examTimes: examTimeErrors
            });
            return;
        }

        if (configuration.autoLogout === 'No' && errors?.submitButtonDisplayMinutes) return;
        if (configuration.autoLogout === 'No') {
            let error = null;
            if (configuration.submitButtonDisplayMinutes.hour() === 0 && configuration.submitButtonDisplayMinutes.minute() === 0) {
                error = 'Please enter valid time.';
                // } else if (configuration.sectionWise === 'Yes' && (configuration.sectionNavigation === 'MOVEMENT_RESTRICTION_WITH_INCREMENTAL_TIME' || configuration.sectionNavigation === 'FIXED_TIME_AND_MOVEMENT_RESTRICTION')) {
                //     let timeDurationInMinutes = 0;
                //     const sectionTime = configuration.sections[configuration.sections.length - 1]?.sectionTime;
                //     if (sectionTime) {
                //         timeDurationInMinutes = sectionTime.hour() * 60 + sectionTime.minute();
                //     }
                //     const submitButtonDisplayMinutesInMinutes = configuration.submitButtonDisplayMinutes.hour() * 60 + configuration.submitButtonDisplayMinutes.minute();
                //     if (timeDurationInMinutes < submitButtonDisplayMinutesInMinutes) {
                //         error = 'Submit button can only be visible in last section.'
                //     }
            } else {
                let timeDurationInMinutes = 0;
                if (configuration.timeDuration) {
                    timeDurationInMinutes = configuration.timeDuration.hour() * 60 + configuration.timeDuration.minute();
                }
                const submitButtonDisplayMinutesInMinutes = configuration.submitButtonDisplayMinutes.hour() * 60 + configuration.submitButtonDisplayMinutes.minute();
                if (timeDurationInMinutes < submitButtonDisplayMinutesInMinutes) {
                    error = 'value can not be greater than time duration.'
                }
            }
            if (error) {
                setErrors({
                    ...errors,
                    submitButtonDisplayMinutes: error
                });
                return;
            }
        }
        if (configuration.feedback === 'Yes') {
            const sectionError = getSectionQuestionsError(configuration.sections);
            if (sectionError) {
                setErrors(prev => ({
                    ...prev,
                    sectionError
                }));
                return;
            }

            if (errors.numberOfFeedbackQuestions) return;
            const numberOfFeedbackQuestions = Number(configuration.numberOfFeedbackQuestions);
            if (isNaN(numberOfFeedbackQuestions) || !numberOfFeedbackQuestions || numberOfFeedbackQuestions === 0) {
                setErrors({
                    ...errors,
                    numberOfFeedbackQuestions: 'Please enter valid number',
                })
                return;
            }
        }
        if (biometricMethodError) return;
        if (configuration.biometricRequired === 'Yes' && configuration.biometricMethod.length === 0) {
            setBiometricMethodError(true);
            return;
        }
        if (configuration.maximumLoginAttempts === 'Yes') {
            if (errors.maximumLoginAttemptsCount) return;
            const maximumLoginAttemptsCount = Number(configuration.maximumLoginAttemptsCount);
            if (isNaN(maximumLoginAttemptsCount) || !maximumLoginAttemptsCount || maximumLoginAttemptsCount === 0) {
                setErrors({
                    ...errors,
                    maximumLoginAttemptsCount: 'Please enter valid number',
                })
                return;
            }
        }
        if (errors?.lateLoginMinutes) return;
        if (configuration.lateLoginAllowed === 'Yes') {
            let error = null;
            if (configuration.lateLoginMinutes.hour() === 0 && configuration.lateLoginMinutes.minute() === 0) {
                error = 'Please enter valid time.'
            } else {
                let timeDurationInMinutes = 0;
                if (configuration.timeDuration) {
                    timeDurationInMinutes = configuration.timeDuration.hour() * 60 + configuration.timeDuration.minute();
                }
                const lateLoginMinutesInMinutes = configuration.lateLoginMinutes.hour() * 60 + configuration.lateLoginMinutes.minute();
                if (timeDurationInMinutes < lateLoginMinutesInMinutes) {
                    error = 'value can not be greater than time duration.'
                }
            }

            if (error) {
                setErrors({
                    ...errors,
                    lateLoginMinutes: error
                });
                return;
            }
        }

        if (errors.remindersCount) return;
        const reminders = configuration.reminders;
        let reminderError = null;
        if (!reminders || Number(reminders) < 0) {
            reminderError = reminders === 0 ? null : 'Please enter valid count.';
        } else if (Number(reminders) > 10) {
            reminderError = 'Reminders can not be more than 10.'
        }
        if (reminderError) {
            setErrors({
                ...errors,
                remindersCount: reminderError
            });
            return;
        }
        let newReminders = configuration.reminders;
        let newReminderErrors = errors.reminders;
        if (newReminders.length !== Number(remindersCount)) {
            const currentReminders = newReminders.length;
            if (currentReminders > remindersCount) {
                newReminders = newReminders.slice(0, remindersCount);
                newReminderErrors = newReminderErrors.slice(0, remindersCount);
            } else {
                for (let i = currentReminders; i < remindersCount; i++) {
                    newReminders.push(dayjs.utc().hour(0).minute(0).second(0).millisecond(0))
                    newReminderErrors.push('Please enter valid time.');
                }
            }
        } else {
            newReminderErrors = errors.reminders.map((rErr, index) => {
                if (!rErr) {
                    const timeDuration = configuration.timeDuration.hour() * 60 + configuration.timeDuration.minute();
                    const reminderTime = newReminders[index].hour() * 60 + newReminders[index].minute();
                    if (reminderTime === 0) {
                        return 'Please enter valid time.';
                    } else if (reminderTime > timeDuration) {
                        return 'Reminder time cannot be greater than time duration.';
                    } else {
                        for (let i = 0; i < configuration.reminders.length; i++) {
                            if (i !== index && configuration.reminders[index].hour() == configuration.reminders[i].hour() && configuration.reminders[index].minute() === configuration.reminders[i].minute()) {
                                return 'Reminder time must be unique.';
                            }
                        }
                    }
                }
                return rErr;
            });
        }
        if (newReminderErrors.some((e) => e)) {
            setConfiguration({
                ...configuration,
                reminders: newReminders
            });
            setErrors({
                ...errors,
                reminders: newReminderErrors
            });
            return;
        }

        if (configuration.numberOfSets < configuration.examTimes.length) {
            setErrors({
                ...errors,
                numberOfSets: 'Number of sets must be same or more than available batches.'
            })
            return;
        }

        if (configuration.examType === 'MCQ') {

            if (configuration.sectionWise === 'Yes') {

                if (errors.sectionError) return;

                for (let sectionErr of errors.sections) {
                    if (
                        sectionErr.titleError ||
                        sectionErr.countError ||
                        sectionErr.subSections.some(e => e)
                    ) return;
                }
                for (let i = 0; i < configuration.sections.length; i++) {
                    const total = Number(configuration.sections[i].noOfQuestions);
                    if (!Number.isInteger(total) || total <= 0) {
                        setErrors(prev => ({
                            ...prev,
                            sections: prev.sections.map((err, idx) =>
                                idx === i
                                    ? { ...err, countError: 'Section questions must be greater than 0' }
                                    : err
                            )
                        }));
                        return;
                    }
                }
                const qpsError = getQuestionsPerSetMismatchError(
                    configuration.sections,
                    configuration.questionsPerSet
                );

                if (qpsError) {
                    setErrors(prev => ({
                        ...prev,
                        sectionError: qpsError
                    }));
                    return;
                }
            }

            if (
                configuration.sectionWise === 'No' &&
                configuration.contentUploadMethod === 'bulk'
            ) {
                if (
                    errors.numberOfEasyQuestions ||
                    errors.numberOfMediumQuestions ||
                    errors.numberOfHardQuestions
                ) return;

                const error = getNumberOfQuestionsError(
                    configuration.questionsPerSet,
                    'questionsPerSet'
                );

                if (error) {
                    setErrors(prev => ({
                        ...prev,
                        numberOfQuestions: error
                    }));
                    return;
                }
            }

        }
        if (configuration.instantResult === 'Yes') {
            if (errors.passingMarks) return;

            const passingMarks = Number(configuration.passingMarks);
            if (!configuration.passingMarks || passingMarks === 0 || isNaN(passingMarks)) {
                setErrors({
                    ...errors,
                    passingMarks: 'Please enter valid value',
                });
                return;
            }

            if (configuration.maxMarks && passingMarks > Number(configuration.maxMarks)) {
                setErrors({
                    ...errors,
                    passingMarks: 'Min marks cannot be greater than max marks',
                });
                return;
            }
        }
        if (errors?.loginBeforeMinutes) return;
        // if (configuration.loginBeforeMinutes.hour() === 0 && configuration.loginBeforeMinutes.minute() === 0) {
        //     setErrors({
        //         ...errors,
        //         loginBeforeMinutes: 'Please enter valid time.'
        //     });
        //     return;
        // }

        if (!configuration.languages.length) {
            setLanguagesError(true);
            return;
        };
        if (configuration.isBilingual === 'Yes') {
            if (configuration.languages.length < 2) {
                setLanguagesError(true);
            }
        } else if (configuration.isBilingual === 'No') {
            if (!configuration.languages.length) {
                setLanguagesError(true);
            }
        }
        if (languagesError) return;

        if (configuration.examType === 'DESCRIPTIVE') {
            if (errors.numberOfAnswerPages) return;

            const numberOfAnswerPages = Number(configuration.numberOfAnswerPages);
            if (isNaN(numberOfAnswerPages) || !numberOfAnswerPages || numberOfAnswerPages === 0) {
                setErrors({
                    ...errors,
                    numberOfAnswerPages: 'Please enter valid number',
                });
                return;
            }
        }

        if (configuration.allowUpload === 'Yes') {
            if (!configuration.uploadType) {
                addNotification('Please select Upload Type.', 'danger');
                return;
            }
            if (configuration.uploadType === 'END_EXAM_UPLOAD') {
                if (errors.uploadDuration) return;
                if (configuration.uploadDuration.hour() === 0 && configuration.uploadDuration.minute() === 0) {
                    setErrors({
                        ...errors,
                        uploadDuration: 'Please enter valid time.',
                    });
                    return;
                }
            }
        }

        setIsSaving(true);
        const body = {
            driveId: drive.id,
            courseId: course.id,
            examType: configuration.examType,
            timeDuration: configuration.timeDuration.format('HH:mm'),
            timeDurationForScribe: configuration.examType === 'TYPING' ? configuration.timeDuration.format('HH:mm') : configuration.timeDurationForScribe.format('HH:mm'),
            startExamBetweenSpecifiedTime: configuration.startExamBetweenSpecifiedTime,
            examTimes: configuration.examTimes.map((examTime) => {
                return {
                    projectMasterId: examTime.projectMasterId,
                    batchId: examTime.batchId,
                    startTime: examTime.startTime.format(),
                    endTime: examTime.endTime.format()
                }
            }),
            unlockWithPassword: configuration.unlockWithPassword === 'Yes',
            proctoringEnabled: configuration.proctoringEnabled === 'Yes',
            ...(configuration.proctoringEnabled === 'Yes' && { proctoringType: configuration.proctoringType, proctoringEngine: configuration.proctoringEngine }),
            ...(configuration.proctoringEnabled === 'Yes' && {
                candidateAuthorizationEnabled: configuration.candidateAuthorizationEnabled === 'Yes',
            }),
            ...(configuration.proctoringEnabled === 'Yes' && {
                selectedViolations: configuration.selectedViolations,
                isViolationAutoSubmitEnabled: configuration.isViolationAutoSubmitEnabled === 'Yes',
                violationThresholdCount: configuration.isViolationAutoSubmitEnabled === 'Yes' ? Number(configuration.violationThresholdCount) : 0,
                triggerViolations: configuration.isViolationAutoSubmitEnabled === 'Yes' ? configuration.triggerViolations : [],
                aiAlertBeepEnabled: configuration.aiAlertBeepEnabled === 'Yes',
            }),
            autoLogout: configuration.autoLogout === 'Yes',
            submitButtonDisplayMinutes: configuration.autoLogout === 'Yes' ? 0 : (configuration.submitButtonDisplayMinutes.hour() * 60) + configuration.submitButtonDisplayMinutes.minute(),
            calculator: (configuration.examType === 'TYPING' || configuration.examType === 'DESCRIPTIVE' || configuration.calculator === 'No') ? 'none' : configuration.calculatorType === 'normal' ? 'normal' : 'scientific',
            provideGraph: configuration.examType === 'DESCRIPTIVE' && configuration.provideGraph === 'Yes',
            // logarithm: configuration.logarithm === 'Yes',
            feedback: configuration.examType === 'DESCRIPTIVE' ? false : configuration.feedback === 'Yes',
            numberOfFeedbackQuestions: configuration.examType === 'DESCRIPTIVE' || configuration.feedback === 'No' ? 0 : configuration.numberOfFeedbackQuestions,
            isBilingual: configuration.isBilingual === 'Yes',
            numberOfSets: configuration.numberOfSets,
            languages: configuration.languages,
            instructionSheets: configuration.instructionSheets === 'Yes',
            biometricRequired: configuration.biometricRequired === 'Yes',
            biometricMethod: configuration.biometricMethod,
            maxMarks: configuration.maxMarks,
            randomizeQuestions: false,
            randomizeOptions: false,
            sampleTestRequired: configuration.sampleTestRequired === 'Yes',
            negativeMarking: configuration.examType !== 'DESCRIPTIVE' && configuration.negativeMarking === 'Yes',
            numberOfAnswerPages: configuration.examType === 'DESCRIPTIVE' ? configuration.numberOfAnswerPages : null,
            maximumLoginAttempts: (configuration.examType !== 'DESCRIPTIVE' && configuration.maximumLoginAttempts === 'Yes') ? configuration.maximumLoginAttemptsCount : null,
            loginBeforeMinutes: configuration.loginBeforeMinutes.format('HH:mm'),
            reminders: configuration.reminders.sort((reminderA, reminderB) => reminderB.unix() - reminderA.unix()).map((reminder) => {
                return reminder.format('HH:mm');
            }),
            forcedLogout: !configuration.startExamBetweenSpecifiedTime && configuration.forcedLogout === 'Yes',
            lateLoginAllowed: !configuration.startExamBetweenSpecifiedTime && configuration.lateLoginAllowed === 'Yes',
            lateLoginMinutes: !configuration.startExamBetweenSpecifiedTime && configuration.lateLoginAllowed === 'Yes' ? (configuration.lateLoginMinutes.hour() * 60) + configuration.lateLoginMinutes.minute() : 0,
            instantScore: configuration.examType !== 'DESCRIPTIVE' && configuration.instantScore === 'Yes',
            instantResult: configuration.examType !== 'DESCRIPTIVE' && configuration.instantResult === 'Yes',
            passingMarks: configuration.instantResult === 'Yes' ? configuration.passingMarks : null,
            allowUpload: configuration.allowUpload === 'Yes',
            uploadType: configuration.allowUpload === 'Yes' ? configuration.uploadType : null,
            uploadDurationInMinutes: configuration.allowUpload === 'Yes' && configuration.uploadType === 'END_EXAM_UPLOAD' ? configuration.uploadDuration.format('HH:mm') : null,
            isSafeExamBrowser: configuration.isSafeExamBrowser,
            isSecureFolder: configuration.isSafeExamBrowser ? configuration.isSecureFolder : false,
            folderType: (configuration.isSafeExamBrowser && configuration.isSecureFolder) ? configuration.folderType : null,
            whitelistedFolderPathWindows: (configuration.isSafeExamBrowser && configuration.isSecureFolder && configuration.folderType === 'local') ? configuration.whitelistedFolderPathWindows : null,
            whitelistedFolderPathMac: (configuration.isSafeExamBrowser && configuration.isSecureFolder && configuration.folderType === 'local') ? configuration.whitelistedFolderPathMac : null,
        }
        // Security settings are sent on create only — they cannot be changed afterwards.
        if (!configurationDetails) {
            body.securityLevel = configuration.securityLevel;
            body.dataEncryptionKeyScope = configuration.securityLevel === 'NONE' ? null : configuration.dataEncryptionKeyScope;
            // true = dedicated KMS keys are created just for this exam configuration
            // false = the client's shared KMS keys are reused (missing ones are created once, then reused)
            body.createConfigurationKmsKey = configuration.securityLevel === 'NONE' ? false : configuration.createConfigurationKmsKey === 'Yes';
        }
        if (configuration.examType === 'MCQ') {
            body.sectionWise = configuration.sectionWise === 'No' ? false : true;
            body.sectionNavigation = configuration.sectionWise === 'No' ? null : configuration.sectionNavigation;
            body.contentUploadMethod = configuration.contentUploadMethod;
            body.numberOfEasyQuestions = configuration.numberOfEasyQuestions;
            body.numberOfMediumQuestions = configuration.numberOfMediumQuestions;
            body.numberOfHardQuestions = configuration.numberOfHardQuestions;
            body.questionsPerSet = configuration.questionsPerSet;
            body.randomizeQuestions = configuration.randomizeQuestions === 'Yes';
            body.randomizeOptions = configuration.randomizeOptions === 'Yes';
            body.allQuestionsMandatory = configuration.allQuestionsMandatory === 'Yes';
            body.questionNavigationForwardOnly = configuration.questionNavigationForwardOnly === 'Yes';
            body.selectionType = configuration.selectionType;

            if (body.sectionWise) {
                body.sectionCount = configuration.sectionCount;
                body.sections = configuration.sections.map((section) => {
                    return {
                        sectionTime: section.sectionTime.format('HH:mm'),
                        sectionTitle: section.sectionTitle?.trim(),
                        noOfQuestions: Number(section.noOfQuestions),
                        subSections: section.subSections.map(ss => ({
                            title: ss.title.trim(),
                            noOfQuestions: Number(ss.noOfQuestions)
                        }))
                    }
                });
            }
        }

        const formBody = new FormData();
        formBody.append('configuration', JSON.stringify(body));
        if (configuration.examType === 'MCQ') {
            formBody.append('removeLogTables', documentFiles.removeLogTablesFile);
            if (documentFiles.logTablesFile) {
                formBody.append('logTablesFile', documentFiles.logTablesFile);
            }
            formBody.append('removePvCharts', documentFiles.removePvChartsFile);
            if (documentFiles.pvChartsFile) {
                formBody.append('pvChartsFile', documentFiles.pvChartsFile);
            }
            formBody.append('removeHeatTable', documentFiles.removeHeatTableFile);
            if (documentFiles.heatTableFile) {
                formBody.append('heatTableFile', documentFiles.heatTableFile);
            }
            formBody.append('removeHelpDocument', documentFiles.removeHelpDocumentFile);
            if (documentFiles.helpDocumentFile) {
                formBody.append('helpDocumentFile', documentFiles.helpDocumentFile);
            }
        }

        if (configurationDetails) {
            try {
                const res = await updateById(configurationDetails.id, formBody)
                if (res.code === 'OK') {
                    addNotification('Configuration updated successfully.', 'success');
                    goToMasters();
                }
            } catch (error) {
                const code = error?.response?.data?.code;
                const securityErrorMessage = getSecurityErrorMessage(error);
                if (code === 'FILE_TOO_LARGE') {
                    addNotification('File size is too large, please make sure all the uploaded files are less than 2Mb in size.', 'danger')
                } else if (securityErrorMessage) {
                    addNotification(securityErrorMessage, 'danger');
                } else {
                    addNotification('Configuration does not updated.', 'danger');
                }
                console.log('There is an error while updating configuration details', error);
            } finally {
                setIsSaving(false);
            }
        } else {
            try {
                const res = await create(formBody);
                if (res.code === 'OK') {
                    addNotification('Configuration created successfully.', 'success')
                    goToMasters();
                }
            } catch (error) {
                const responseData = error?.response?.data?.data;
                const code = error?.response?.data?.code;
                const securityErrorMessage = getSecurityErrorMessage(error);
                if (code === 'FILE_TOO_LARGE') {
                    addNotification('File size is too large, please make sure all the uploaded files are less than 2Mb in size.', 'danger')
                } else if (securityErrorMessage) {
                    addNotification(securityErrorMessage, 'danger');
                } else if (responseData?.status === 422 && responseData?.code === 'UNPROCESSABLE_ENTITY') {
                    addNotification('Configuration already exists.', 'danger');
                } else {
                    addNotification(error?.response?.data?.message || 'Configuration does not created.', 'danger');
                }
                console.log('There is an error while creating configuration', error);
            } finally {
                setIsSaving(false);
            }
        }
    }

    const handleSubSectionsCountChange = (sectionIndex, value) => {
        let newCount = value;
        if (newCount) {
            if (Number(newCount) < 0) {
                newCount = 0;
            } else if (Number(newCount) > 20) {
                newCount = 20;
            }
        }
        setConfiguration({
            ...configuration,
            sections: configuration.sections.map((section, index) => {
                if (sectionIndex === index) {
                    return {
                        ...section,
                        subSectionsCount: newCount
                    }
                }
                return section;
            })
        });
        setErrors({
            ...errors,
            sections: errors.sections.map((sectionErr, index) => {
                if (index === sectionIndex) {
                    return {
                        ...sectionErr,
                        countError: !newCount ? 'Please enter valid number' : null
                    }
                }
                return sectionErr;
            })
        })
    }
    const handleSubSectionInputCheck = (sectionIndex) => {
        const newCount = configuration.sections[sectionIndex].subSectionsCount;
        const oldCount = configuration.sections[sectionIndex].subSections.length;
        if (newCount === oldCount) return;

        let newSubSections = [];
        let newSubSectionErrors = [];
        if (newCount < oldCount) {
            newSubSections = configuration.sections.map((section, index) => {
                if (sectionIndex === index) {
                    return {
                        ...section,
                        subSections: section.subSections.slice(0, newCount)
                    }
                }
                return section;
            });
            newSubSectionErrors = errors.sections.map((sectionErr, index) => {
                if (sectionIndex === index) {
                    return {
                        ...sectionErr,
                        subSections: sectionErr.subSections.slice(0, newCount)
                    }
                }
                return sectionErr;
            });
        } else {
            newSubSections = configuration.sections.map((section, index) => {
                if (sectionIndex === index) {
                    const subSections = section.subSections;
                    for (let i = oldCount; i < newCount; i++) {
                        subSections.push({
                            title: `Sub Section ${i + 1}`,
                            noOfQuestions: ''
                        });
                    }
                    section.subSections = subSections;
                }
                return section;
            });
            newSubSectionErrors = errors.sections.map((sectionErr, index) => {
                if (sectionIndex === index) {
                    const subSections = sectionErr.subSections;
                    for (let i = oldCount; i < newCount; i++) {
                        subSections.push(null);
                    }
                    sectionErr.subSections = subSections;
                }
                return sectionErr;
            });
        }
        setConfiguration({
            ...configuration,
            sections: newSubSections
        });
        setErrors({
            ...errors,
            sections: newSubSectionErrors
        });
    }

    const handleSubSectionTitleChange = (sectionIndex, subSectionIndex, value) => {
        const newSections = configuration.sections.map((section, index) => {
            if (sectionIndex === index) {
                return {
                    ...section,
                    subSections: section.subSections.map((subSection, i) => {
                        if (subSectionIndex === i) {
                            if (typeof subSection === 'string') {
                                return {
                                    title: value,
                                    noOfQuestions: 0
                                };
                            }
                            return {
                                ...subSection,
                                title: value
                            };
                        }
                        return subSection;
                    })
                }
            }
            return section;
        });
        setConfiguration({
            ...configuration,
            sections: newSections
        });
        let error = null;
        if (!value) {
            error = 'Please enter valid sub section title.';
        }

        setErrors({
            ...errors,
            sections: errors.sections.map((sectionErr, i) => {
                if (sectionIndex === i) {
                    return {
                        ...sectionErr,
                        subSections: sectionErr.subSections.map((subSectionErr, j) => {
                            if (subSectionIndex === j) {
                                return error;
                            }
                            return subSectionErr;
                        })
                    };
                }
                return sectionErr;
            })
        });
    };

    const handleSubSectionNoOfQuestionsChange = (sectionIndex, subSectionIndex, value) => {
        const newSections = configuration.sections.map((section, index) => {
            if (sectionIndex !== index) return section;
            if (sectionIndex === index) {
                return {
                    ...section,
                    subSections: section.subSections.map((subSection, i) => {

                        if (subSectionIndex === i) {
                            return {
                                ...(typeof subSection === 'string'
                                    ? { title: subSection }
                                    : subSection),
                                noOfQuestions: value
                            };
                        }
                        return subSection;
                    })
                };
            }
            return section;
        });

        setConfiguration({
            ...configuration,
            sections: newSections
        });

        let error = null;

        if (value === '') {
            error = 'Please enter number of questions';
        } else if (!/^[1-9][0-9]*$/.test(value.trim())) {
            error = 'Value must be greater than 0';
        }
        else {
            error = null;
        }

        const sectionError = getSectionQuestionsError(newSections);
        const qpsError = getQuestionsPerSetMismatchError(newSections, configuration.questionsPerSet);

        setErrors({
            ...errors,
            sectionError: sectionError || qpsError,
            sections: errors.sections.map((secErr, i) =>
                i === sectionIndex
                    ? {
                        ...secErr,
                        subSections: secErr.subSections.map((sErr, j) =>
                            j === subSectionIndex ? error : sErr
                        )
                    }
                    : secErr
            )
        });
    };

    const getSectionQuestionsError = (sections) => {
        for (let section of sections) {
            if (!section.subSections || section.subSections.length === 0) continue;

            const sectionTotal = Number(section.noOfQuestions || 0);
            if (!sectionTotal || sectionTotal <= 0) {
                return `Section "${section.sectionTitle}" must have questions greater than 0`;
            }

            const subTotal = section.subSections.reduce(
                (sum, ss) => sum + Number(ss.noOfQuestions || 0),
                0
            );

            if (subTotal !== sectionTotal) {
                return `Section "${section.sectionTitle}" total (${sectionTotal}) must match subsections total (${subTotal})`;
            }
        }
        return null;
    };

    const handlePassingMarksChange = (e) => {
        let value = e.target.value;
        if (value) {
            if (Number(value) < 0) {
                value = 0;
            } else if (Number(value) > 1000) {
                value = 1000;
            }
        }

        let error = null;
        if (!value || Number(value) === 0) {
            error = 'Please enter valid value';
        } else if (configuration.maxMarks && Number(value) > Number(configuration.maxMarks)) {
            error = 'Min marks cannot be greater than max marks';
        }

        setErrors({
            ...errors,
            passingMarks: error
        });
        setConfiguration({
            ...configuration,
            passingMarks: value,
        });
    };

    return (
        <MainContainer>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography sx={{ fontWeight: 400, textTransform: 'capitalize' }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>Exam Configuration</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>

            <Container>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: 'calc(100vh - 116px)'
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        columnGap: 2,
                        rowGap: 0.5,
                        mb: 3
                    }}>
                        <TextField
                            label="Drive Name"
                            variant="outlined"
                            value={drive['driveName']}
                            disabled
                            InputLabelProps={{
                                shrink: true,
                            }}
                            sx={{
                                width: 238,
                                bgcolor: 'background.base'
                            }}
                        />
                        <TextField
                            label="Course Name"
                            variant="outlined"
                            value={course['courseName']}
                            disabled
                            InputLabelProps={{
                                shrink: true
                            }}
                            sx={{
                                width: 238,
                                bgcolor: 'background.base'
                            }}
                        />
                        <Box sx={{ ml: 'auto' }}>


                            {<RadiusButton sx={{
                                borderRadius: 40,
                                textTransform: 'capitalize'
                            }} color="grey"
                                variant='contained'
                                size='large'
                                onClick={goToMasters}
                                startIcon={<img src={ARROW_LEFT} alt='' />}
                            >Go back</RadiusButton>}
                            {(canEditLockedConfig) && <LoadingButton sx={{
                                borderRadius: 40,
                                ml: 1,
                                textTransform: 'capitalize'
                            }} color="primary"
                                variant='contained'
                                size='large'
                                // disabled={hasError}
                                onClick={handleSubmitForm}
                                loading={isSaving}
                            >Save</LoadingButton>}
                        </Box>
                    </Box>

                    {(isExamEnabled) && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            px: 2,
                            py: 1,
                            mb: 1,
                            bgcolor: 'warning.light',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'warning.main',
                        }}>
                            <Typography variant='body2' sx={{ color: 'warning.dark', fontWeight: 500 }}>
                                {isExamEnabled && canEditLockedConfig ? 'This exam is enabled (running).' : 'This exam is ended.'}
                                {canEditLockedConfig
                                    ? ' You can edit exam date/time, maximum login attempts, login before time, and late login settings.'
                                    : ' Configuration cannot be modified.'}
                            </Typography>
                        </Box>
                    )}

                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
                            <Tab label="Pre Exam Configuration" {...a11yProps(0)} />
                            <Tab label="Exam Configuration" {...a11yProps(1)} />
                            {configuration.examType !== 'DESCRIPTIVE'
                                && <Tab label="Post Exam Configuration" {...a11yProps(2)} />
                            }
                        </Tabs>
                    </Box>
                    <CustomTabPanel value={tabValue} index={0}>
                        <TableContainer sx={{
                            p: 2,
                            pt: 0.25,
                            maxHeight: 'inherit',
                            maxWidth: 'inherit',
                            // overflow: 'auto',
                            borderRadius: 1
                        }}>
                            <Table stickyHeader aria-label="table">
                                <TableBody>
                                    <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Question Type
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <FormControl sx={{ width: '100%' }}>
                                                <InputLabel id="questionTypeLabel">Question Type</InputLabel>
                                                <Select
                                                    id="examType"
                                                    labelId="questionTypeLabel"
                                                    label='Exam Type'
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 320,
                                                        textTransform: 'capitalize',
                                                    }}
                                                    disabled={isExamEnabled || contentUploaded || isLocked ? true : false}
                                                    value={configuration.examType}
                                                    onChange={handleExamTypeChange}
                                                    input={
                                                        <OutlinedInput label='Exam Type' />
                                                    }
                                                >
                                                    <MenuItem value='MCQ'>MCQ</MenuItem>
                                                    <MenuItem value='DESCRIPTIVE'>Paperless</MenuItem>
                                                    <MenuItem value='TYPING'>Typing</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', color: 'primary.main' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.examType === 'MCQ' ? 'MCQ' : configuration.examType === 'DESCRIPTIVE' ? 'Paperless' : 'Typing'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Is Safe Exam Browser
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <RadioGroup
                                                row
                                                value={configuration.isSafeExamBrowser ? 'true' : 'false'}
                                                onChange={(e) => {
                                                    const enabled = e.target.value === 'true';
                                                    setConfiguration({
                                                        ...configuration,
                                                        isSafeExamBrowser: enabled,
                                                        isSecureFolder: false,
                                                        folderType: null,
                                                        whitelistedFolderPathWindows: '',
                                                        whitelistedFolderPathMac: '',
                                                    });
                                                    if (!enabled) {
                                                        setErrors(prev => ({ ...prev, whitelistedFolderPathWindows: null, whitelistedFolderPathMac: null }));
                                                    }
                                                }}
                                            >
                                                <FormControlLabel value='true' control={<Radio />} label='Yes' disabled={isExamEnabled || isLocked} />
                                                <FormControlLabel value='false' control={<Radio />} label='No' disabled={isExamEnabled || isLocked} />
                                            </RadioGroup>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', color: 'primary.main' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.isSafeExamBrowser ? 'Yes' : 'No'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    {configuration.isSafeExamBrowser && (
                                        <TableRow>
                                            <TableCell>
                                                <Typography component='span' variant='subtitle1'>
                                                    Secure Folder
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <RadioGroup
                                                    row
                                                    value={configuration.isSecureFolder ? 'true' : 'false'}
                                                    onChange={(e) => {
                                                        const enabled = e.target.value === 'true';
                                                        setConfiguration({
                                                            ...configuration,
                                                            isSecureFolder: enabled,
                                                            folderType: enabled ? 'local' : null,
                                                            whitelistedFolderPathWindows: '',
                                                            whitelistedFolderPathMac: '',
                                                        });
                                                        if (!enabled) {
                                                            setErrors(prev => ({ ...prev, whitelistedFolderPathWindows: null, whitelistedFolderPathMac: null }));
                                                        }
                                                    }}
                                                >
                                                    <FormControlLabel value='true' control={<Radio />} label='Yes' disabled={isExamEnabled || isLocked} />
                                                    <FormControlLabel value='false' control={<Radio />} label='No' disabled={isExamEnabled || isLocked} />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', color: 'primary.main' }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    {configuration.isSecureFolder ? 'Yes' : 'No'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {configuration.isSafeExamBrowser && configuration.isSecureFolder && (
                                        <TableRow>
                                            <TableCell>
                                                <Typography component='span' variant='subtitle1'>
                                                    Reference Material Location
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <RadioGroup
                                                    row
                                                    value={configuration.folderType || ''}
                                                    onChange={(e) => {
                                                        const type = e.target.value;
                                                        setConfiguration({
                                                            ...configuration,
                                                            folderType: type,
                                                            whitelistedFolderPathWindows: '',
                                                            whitelistedFolderPathMac: '',
                                                        });
                                                        setErrors(prev => ({ ...prev, whitelistedFolderPathWindows: null, whitelistedFolderPathMac: null }));
                                                    }}
                                                >
                                                    <FormControlLabel value='local' control={<Radio />} label='Local Folder' disabled={isExamEnabled || isLocked} />
                                                    <FormControlLabel value='cloud' control={<Radio />} label='Cloud Folder' disabled={isExamEnabled || isLocked} />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', color: 'primary.main' }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    {configuration.folderType === 'local' ? 'Local Folder' : configuration.folderType === 'cloud' ? 'Cloud Folder' : '—'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {configuration.isSafeExamBrowser && configuration.isSecureFolder && configuration.folderType === 'local' && (
                                        <TableRow>
                                            <TableCell colSpan={3}>
                                                <Typography variant='body2' sx={{ color: 'warning.main', fontStyle: 'italic' }}>
                                                    System will allow only configured allowed paths for Windows and Mac.
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {configuration.isSafeExamBrowser && configuration.isSecureFolder && configuration.folderType === 'local' && (
                                        <TableRow>
                                            <TableCell>
                                                <Typography component='span' variant='subtitle1'>
                                                    Whitelisted Reference Path for Windows
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size='small'
                                                    sx={{ width: '100%', maxWidth: 320 }}
                                                    placeholder='e.g. C:\ExamFolder'
                                                    value={configuration.whitelistedFolderPathWindows}
                                                    disabled={isExamEnabled || isLocked}
                                                    error={!!errors.whitelistedFolderPathWindows}
                                                    helperText={errors.whitelistedFolderPathWindows}
                                                    onChange={(e) => setConfiguration({
                                                        ...configuration,
                                                        whitelistedFolderPathWindows: e.target.value,
                                                    })}
                                                    onFocus={() => setErrors({ ...errors, whitelistedFolderPathWindows: null })}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', color: 'primary.main', maxWidth: 180 }}>
                                                <Tooltip title={configuration.whitelistedFolderPathWindows || ''} disableHoverListener={!configuration.whitelistedFolderPathWindows}>
                                                    <Typography component='span' variant='subtitle1' sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {configuration.whitelistedFolderPathWindows || '—'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {configuration.isSafeExamBrowser && configuration.isSecureFolder && configuration.folderType === 'local' && (
                                        <TableRow>
                                            <TableCell>
                                                <Typography component='span' variant='subtitle1'>
                                                    Whitelisted Reference Path for Mac
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    size='small'
                                                    sx={{ width: '100%', maxWidth: 320 }}
                                                    placeholder='e.g. /Users/exam/ExamFolder'
                                                    value={configuration.whitelistedFolderPathMac}
                                                    disabled={isExamEnabled || isLocked}
                                                    error={!!errors.whitelistedFolderPathMac}
                                                    helperText={errors.whitelistedFolderPathMac}
                                                    onChange={(e) => setConfiguration({
                                                        ...configuration,
                                                        whitelistedFolderPathMac: e.target.value,
                                                    })}
                                                    onFocus={() => setErrors({ ...errors, whitelistedFolderPathMac: null })}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', color: 'primary.main', maxWidth: 180 }}>
                                                <Tooltip title={configuration.whitelistedFolderPathMac || ''} disableHoverListener={!configuration.whitelistedFolderPathMac}>
                                                    <Typography component='span' variant='subtitle1' sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {configuration.whitelistedFolderPathMac || '—'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Batch Duration
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <TimeField
                                                className='time-input'
                                                defaultValue={configuration.timeDuration}
                                                value={configuration.timeDuration}
                                                format="HH:mm"
                                                disabled={isExamEnabled ? true : false}
                                                onChange={(d, e) => handleTimeDurationChange(d, e)}
                                                error={!!errors.timeDuration}
                                                slotProps={{
                                                    textField: {
                                                        helperText: errors.timeDuration,
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', color: 'primary.main' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {`${configuration.timeDuration.format('HH')} Hours ${configuration.timeDuration.format('mm')} Minutes`}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>

                                    {configuration.examType !== 'TYPING' && <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Batch Duration for scribe candidate
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <TimeField
                                                className='time-input'
                                                defaultValue={configuration.timeDurationForScribe}
                                                value={configuration.timeDurationForScribe}
                                                format="HH:mm"
                                                disabled={isExamEnabled ? true : false}
                                                error={!!errors.timeDurationForScribe}
                                                onChange={handleTimeDurationForScribeChange}
                                                slotProps={{
                                                    textField: {
                                                        helperText: errors.timeDurationForScribe,
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', color: 'primary.main' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {`${configuration.timeDurationForScribe.format('HH')} Hours ${configuration.timeDurationForScribe.format('mm')} Minutes`}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>}

                                    {
                                        configuration.examTimes.length
                                            ? <>
                                                <TableRow sx={{ pt: 1 }}>
                                                    <TableCell sx={{ p: 0, pt: 1, border: 'none' }}></TableCell>
                                                    <TableCell sx={{ p: 0, pt: 1, border: 'none', maxWidth: 220 }}>
                                                        <FormControlLabel
                                                            disabled={canEditNow ? false : (isExamEnabled ? true : false)}
                                                            control={
                                                                <Checkbox
                                                                    size='small'
                                                                    checked={configuration.startExamBetweenSpecifiedTime}
                                                                    onChange={handleStartExamBetweenSpecifiedTimeChange}
                                                                />
                                                            }
                                                            sx={{
                                                                maxWidth: 280,
                                                                '& .MuiTypography-root': {
                                                                    color: 'primary.main',
                                                                    fontSize: 13,
                                                                }
                                                            }}
                                                            label="Allow candidates to start exam between specified exam start/end time"
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ p: 0, pt: 1, border: 'none' }}></TableCell>
                                                </TableRow>
                                                {configuration.examTimes.map((examTime, index) => {
                                                    return <TableRow key={index}>
                                                        <TableCell sx={{ pt: index ? 0 : '', border: (index === configuration.examTimes.length - 1) ? '' : 'none', verticalAlign: 'top' }}>
                                                            {index === 0 && <Typography component='span' variant='subtitle1'>
                                                                Batch Date and Time
                                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                            </Typography>}
                                                        </TableCell>
                                                        <TableCell sx={{ pt: index ? 0 : '', border: (index === configuration.examTimes.length - 1) ? '' : 'none' }}>
                                                            <Typography component='div' sx={{ mb: 1 }} variant='subtitle1'>
                                                                {`${examTime.batchName}`}
                                                            </Typography>
                                                            <Box>
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <DatePicker
                                                                        size='small'
                                                                        label='Start Date'
                                                                        InputLabelProps={{
                                                                            shrink: 'true'
                                                                        }}
                                                                        disabled={examTime.hasExamConfig ? true : (canEditNow ? false : true)}
                                                                        format='DD-MM-YYYY'
                                                                        minDate={dayjs()}
                                                                        value={examTime.startTime}
                                                                        onChange={(val, err) => handleExamStartDateChange(index, val, err)}
                                                                        sx={{
                                                                            flexGrow: 1,
                                                                            maxWidth: 220,
                                                                            '& .MuiInputBase-input': {
                                                                                padding: '9px 14px !important',
                                                                            },
                                                                            '& .MuiFormLabel-root.Mui-error': {
                                                                                color: 'rgba(0, 0, 0, 0.6)'
                                                                            },
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: 'rgba(0, 0, 0, 0.23) !important'
                                                                            }
                                                                        }} />
                                                                    <TimeField
                                                                        size='small'
                                                                        sx={{ width: 80 }}
                                                                        value={examTime.startTime}
                                                                        disabled={examTime.hasExamConfig ? true : (canEditNow ? false : (isExamEnabled ? true : false))}
                                                                        label='Time'
                                                                        format="HH:mm"
                                                                        minTime={examTime.startTime && dayjs(examTime.startTime).isSame(dayjs(), 'day') ? dayjs() : undefined}
                                                                        onChange={(v, e) => handleExamStartTimeChange(v, e, index)}
                                                                        error={!!errors?.['examTimes']?.[index]?.startTime}
                                                                    />
                                                                </Box>
                                                                {errors?.['examTimes']?.[index].startTime
                                                                    && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>{errors?.['examTimes']?.[index].startTime}</FormHelperText>}
                                                            </Box>
                                                            {configuration.startExamBetweenSpecifiedTime && <Box sx={{ mt: 2 }}>
                                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                                    <DatePicker
                                                                        size='small'
                                                                        label='End Date'
                                                                        InputLabelProps={{
                                                                            shrink: 'true'
                                                                        }}
                                                                        format='DD-MM-YYYY'
                                                                        value={examTime.endTime}
                                                                        minDate={examTime.startTime && examTime.startTime.isAfter(dayjs()) ? examTime.startTime : dayjs()}
                                                                        onChange={(val, err) => handleExamEndDateChange(index, val, err)}
                                                                        disabled={canEditNow ? false : (isExamEnabled ? true : false)}
                                                                        sx={{
                                                                            flexGrow: 1,
                                                                            maxWidth: 220,
                                                                            '& .MuiInputBase-input': {
                                                                                padding: '9px 14px !important',
                                                                            },
                                                                            '& .MuiFormLabel-root.Mui-error': {
                                                                                color: 'rgba(0, 0, 0, 0.6)'
                                                                            },
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: 'rgba(0, 0, 0, 0.23) !important'
                                                                            }
                                                                        }} />
                                                                    <TimeField
                                                                        size='small'
                                                                        sx={{ width: 80 }}
                                                                        value={examTime.endTime}
                                                                        disabled={canEditNow ? false : (isExamEnabled ? true : false)}
                                                                        label='Time'
                                                                        format="HH:mm"
                                                                        minTime={examTime.endTime && dayjs(examTime.endTime).isSame(dayjs(), 'day') ? dayjs() : undefined}
                                                                        onChange={(v, e) => handleExamEndTimeChange(v, e, index)}
                                                                        error={!!errors?.['examTimes']?.[index]?.endTime}
                                                                    />
                                                                </Box>
                                                                {errors?.['examTimes']?.[index].endTime
                                                                    ? <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                                        {errors?.['examTimes'][index].endTime}
                                                                    </FormHelperText>
                                                                    : null}
                                                            </Box>}

                                                        </TableCell>
                                                        <TableCell sx={{ maxWidth: 250, pt: index ? 0 : '', border: (index === configuration.examTimes.length - 1) ? '' : 'none', textAlign: 'right', color: 'primary.main' }}>
                                                            <Typography component='span' variant='subtitle1'>
                                                                {errors?.['examTimes']?.[index].startTime
                                                                    ? null
                                                                    : <Box component='div' sx={{ display: 'grid', columnGap: 1, gridTemplateColumns: '1fr auto' }}>
                                                                        <span>Exam start date: </span>
                                                                        <span>{examTime.startTime.format('DD-MM-YYYY')}</span>
                                                                        <span>Exam start time: </span>
                                                                        <span>{examTime.startTime.format('hh:mm a')}</span>
                                                                        <span>Exam end date: </span>
                                                                        <span>{examTime.endTime.format('DD-MM-YYYY')}</span>
                                                                        <span>Exam end time: </span>
                                                                        <span>{examTime.endTime.format('hh:mm a')}</span>
                                                                    </Box>}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                })}
                                            </>
                                            : null
                                    }

                                    <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Security Key Required for Content?
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <RadioGroup row name="password-key-for-unlock" onChange={(e, value) => handleConfigChange('unlockWithPassword', value)}>
                                                <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.unlockWithPassword === 'Yes'}
                                                    />
                                                } label="Yes" />
                                                <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.unlockWithPassword === 'No'}
                                                    />
                                                } label="No" />
                                            </RadioGroup>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', color: 'primary.main' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.unlockWithPassword}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ borderColor: configuration.allowUpload === 'Yes' ? 'transparent !important' : '' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                Is Allow Upload
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ borderColor: configuration.allowUpload === 'Yes' ? 'transparent !important' : '' }}>
                                            <RadioGroup row name="allow-upload" onChange={(e, value) => {
                                                setConfiguration({
                                                    ...configuration,
                                                    allowUpload: value,
                                                    uploadType: value === 'No' ? null : configuration.uploadType,
                                                    uploadDuration: value === 'No' ? dayjs.utc().hour(0).minute(0).second(0).millisecond(0) : configuration.uploadDuration,
                                                });
                                                if (value === 'No') {
                                                    setErrors({ ...errors, uploadDuration: null });
                                                }
                                            }}>
                                                <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                    <Radio
                                                        disabled={isExamEnabled || isLocked ? true : false}
                                                        checked={configuration.allowUpload === 'Yes'}
                                                    />
                                                } label="Yes" />
                                                <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                    <Radio
                                                        disabled={isExamEnabled || isLocked ? true : false}
                                                        checked={configuration.allowUpload === 'No'}
                                                    />
                                                } label="No" />
                                            </RadioGroup>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', color: 'primary.main', borderColor: configuration.allowUpload === 'Yes' ? 'transparent !important' : '' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.allowUpload}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    {configuration.allowUpload === 'Yes' && (
                                        <TableRow>
                                            <TableCell sx={{ borderColor: configuration.uploadType === 'END_EXAM_UPLOAD' ? 'transparent !important' : '' }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    Upload Type
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderColor: configuration.uploadType === 'END_EXAM_UPLOAD' ? 'transparent !important' : '' }}>
                                                <RadioGroup row name="upload-type" onChange={(e, value) => {
                                                    setConfiguration({
                                                        ...configuration,
                                                        uploadType: value,
                                                        uploadDuration: value !== 'END_EXAM_UPLOAD' ? dayjs.utc().hour(0).minute(0).second(0).millisecond(0) : configuration.uploadDuration,
                                                    });
                                                    if (value !== 'END_EXAM_UPLOAD') {
                                                        setErrors({ ...errors, uploadDuration: null });
                                                    }
                                                }}>
                                                    <FormControlLabel sx={{ width: 165 }} value='INLINE_UPLOAD' control={
                                                        <Radio
                                                            disabled={isExamEnabled || isLocked ? true : false}
                                                            checked={configuration.uploadType === 'INLINE_UPLOAD'}
                                                        />
                                                    } label="Inline Upload" />
                                                    <FormControlLabel sx={{ width: 165 }} value='END_EXAM_UPLOAD' control={
                                                        <Radio
                                                            disabled={isExamEnabled || isLocked ? true : false}
                                                            checked={configuration.uploadType === 'END_EXAM_UPLOAD'}
                                                        />
                                                    } label="End Exam Upload" />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', color: 'primary.main', borderColor: configuration.uploadType === 'END_EXAM_UPLOAD' ? 'transparent !important' : '' }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    {configuration.uploadType === 'INLINE_UPLOAD' ? 'Inline Upload' : configuration.uploadType === 'END_EXAM_UPLOAD' ? 'End Exam Upload' : ''}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {configuration.allowUpload === 'Yes' && configuration.uploadType === 'END_EXAM_UPLOAD' && (
                                        <TableRow>
                                            <TableCell>
                                                <Typography component='span' variant='subtitle1'>
                                                    Allow Upload Time
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <TimeField
                                                    className='time-input'
                                                    sx={{ mt: 1.5 }}
                                                    value={configuration.uploadDuration}
                                                    format="HH:mm"
                                                    disabled={isExamEnabled || isLocked ? true : false}
                                                    onChange={(value, err) => {
                                                        const error = err.validationError ? 'Please enter valid time.' : null;
                                                        setErrors({ ...errors, uploadDuration: error });
                                                        if (value) {
                                                            setConfiguration({ ...configuration, uploadDuration: value });
                                                        }
                                                    }}
                                                    error={!!errors.uploadDuration}
                                                    slotProps={{
                                                        textField: {
                                                            helperText: errors.uploadDuration,
                                                        }
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', color: 'primary.main' }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    {configuration.uploadDuration && !(configuration.uploadDuration.hour() === 0 && configuration.uploadDuration.minute() === 0)
                                                        ? `${configuration.uploadDuration.format('HH')} Hours ${configuration.uploadDuration.format('mm')} Minutes`
                                                        : ''}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    <TableRow>
                                        {/* LEFT COLUMN */}
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                            <Typography component="span" variant="subtitle1" sx={{ lineHeight: 2.5 }}>
                                                Proctoring Enabled
                                                <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                            <RadioGroup
                                                row
                                                name="proctoring-enable"
                                                onChange={(e, value) =>
                                                    setConfiguration({
                                                        ...configuration,
                                                        proctoringEnabled: value,
                                                        ...(value === 'No' && { proctoringType: undefined , selectedViolations: [], triggerViolations: [], candidateAuthorizationEnabled: 'No' }),
                                                        ...(value === 'Yes' && !configuration.selectedViolations.length && {
                                                            selectedViolations: DEFAULT_NEW_PROCTORING_VIOLATIONS,
                                                        }),
                                                    })
                                                }
                                            >
                                                <FormControlLabel
                                                    sx={{ width: 165 }}
                                                    value="Yes"
                                                    control={
                                                        <Radio
                                                            disabled={isExamEnabled}
                                                            checked={configuration.proctoringEnabled === 'Yes'}
                                                        />
                                                    }
                                                    label="Yes"
                                                />
                                                <FormControlLabel
                                                    sx={{ width: 165 }}
                                                    value="No"
                                                    control={
                                                        <Radio
                                                            disabled={isExamEnabled}
                                                            checked={configuration.proctoringEnabled === 'No'}
                                                        />
                                                    }
                                                    label="No"
                                                />
                                            </RadioGroup>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                            <Typography component="span" variant="subtitle1">
                                                {configuration.proctoringEnabled}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>

                                    {configuration.proctoringEnabled === 'Yes' && (
                                        <TableRow>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <Typography component="span" variant="subtitle1" sx={{ lineHeight: 2.5 }}>
                                                    Proctoring Type
                                                    <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <RadioGroup
                                                    row
                                                    name="proctoring-type"
                                                    value={configuration.proctoringType || ''}
                                                    onChange={(e, value) =>
                                                        setConfiguration({
                                                            ...configuration,
                                                            proctoringType: value,
                                                            ...(value === APP_CONSTANTS.PROCTORING_TYPE.LIVE && { candidateAuthorizationEnabled: 'Yes' }),
                                                        })
                                                    }
                                                >
                                                    <FormControlLabel
                                                        sx={{ width: 165 }}
                                                        value={APP_CONSTANTS.PROCTORING_TYPE.LITE}
                                                        control={<Radio disabled={isExamEnabled} />}
                                                        label="Lite Proctoring"
                                                    />
                                                    <FormControlLabel
                                                        sx={{ width: 165 }}
                                                        value={APP_CONSTANTS.PROCTORING_TYPE.LIVE}
                                                        control={<Radio disabled={isExamEnabled} />}
                                                        label="Live Proctoring"
                                                    />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                                <Typography component="span" variant="subtitle1">
                                                    {configuration.proctoringType || '-'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {configuration.proctoringEnabled === 'Yes' && (
                                        <TableRow>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <Typography component="span" variant="subtitle1" sx={{ lineHeight: 2.5 }}>
                                                     Candidate Authorization 
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <RadioGroup
                                                    row
                                                    name="candidate-authorization-enabled"
                                                    value={configuration.candidateAuthorizationEnabled || 'No'}
                                                    onChange={(e, value) =>
                                                        setConfiguration({
                                                            ...configuration,
                                                            candidateAuthorizationEnabled: value,
                                                            ...(value === 'Yes' && { proctoringEngine: APP_CONSTANTS.PROCTORING_ENGINE.ULTRA_LITE }),
                                                        })
                                                    }
                                                >
                                                    <FormControlLabel
                                                        sx={{ width: 165 }}
                                                        value="Yes"
                                                        control={<Radio disabled={isExamEnabled || configuration.proctoringType === APP_CONSTANTS.PROCTORING_TYPE.LIVE} />}
                                                        label="Yes"
                                                    />
                                                    <FormControlLabel
                                                        sx={{ width: 165 }}
                                                        value="No"
                                                        control={<Radio disabled={isExamEnabled || configuration.proctoringType === APP_CONSTANTS.PROCTORING_TYPE.LIVE} />}
                                                        label="No"
                                                    />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                                <Typography component="span" variant="subtitle1">
                                                    {configuration.candidateAuthorizationEnabled || 'No'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {configuration.proctoringEnabled === 'Yes' && (
                                        <TableRow>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <Typography component="span" variant="subtitle1" sx={{ lineHeight: 2.5 }}>
                                                    Proctoring Engine
                                                    <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top' }}>
                                                <RadioGroup
                                                    row
                                                    name="proctoring-engine"
                                                    value={configuration.proctoringEngine || ''}
                                                    onChange={(e, value) =>
                                                        setConfiguration({
                                                            ...configuration,
                                                            proctoringEngine: value,
                                                        })
                                                    }
                                                >
                                                    <FormControlLabel
                                                        sx={{ width: 165 }}
                                                        value={APP_CONSTANTS.PROCTORING_ENGINE.AI}
                                                        control={<Radio disabled={isExamEnabled} />}
                                                        label="AI"
                                                    />
                                                    <FormControlLabel
                                                        sx={{ width: 165 }}
                                                        value={APP_CONSTANTS.PROCTORING_ENGINE.ULTRA_LITE}
                                                        control={<Radio disabled={isExamEnabled} />}
                                                        label="ULTRA LITE"
                                                    />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                                <Typography component="span" variant="subtitle1">
                                                    {configuration.proctoringEngine || '-'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {configuration.proctoringEnabled === 'Yes' && (
                                        <>
                                            {/* Row 1: Violation Selection */}
                                            <TableRow>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <Typography component="span" variant="subtitle1" sx={{ lineHeight: 2.5 }}>
                                                        Violation Selection
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <FormControl sx={{ width: '100%' }}>
                                                        <InputLabel id="violationSelectLabel">Violation Selection</InputLabel>
                                                        <Select
                                                            labelId="violationSelectLabel"
                                                            multiple
                                                            sx={{ width: '100%', maxWidth: 320 }}
                                                            disabled={isExamEnabled}
                                                            value={configuration.selectedViolations}
                                                            onChange={handleSelectedViolationsChange}
                                                            input={<OutlinedInput label="Violation Selection" />}
                                                            renderValue={(selected) => (
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                                    {selected.map((wc) => {
                                                                        const v = availableViolations.find(av => av.warningCode === wc);
                                                                        return (
                                                                            <Box key={wc} sx={{ display: 'flex', gap: 1.5, bgcolor: 'primary.50', py: 0.5, px: 1.5, borderRadius: 1 }}>
                                                                                <span>{v?.description || wc}</span>
                                                                            </Box>
                                                                        );
                                                                    })}
                                                                </Box>
                                                            )}
                                                        >
                                                            {availableViolations.map((v) => (
                                                                <MenuItem key={v.warningCode} value={v.warningCode}>
                                                                    {v.description} ({v.warningCode})
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                        {violationErrors.selectedViolations && (
                                                            <FormHelperText sx={{ color: 'error.main' }}>
                                                                {violationErrors.selectedViolations}
                                                            </FormHelperText>
                                                        )}
                                                    </FormControl>
                                                </TableCell>
                                                {/* <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                                    <Typography component="span" variant="subtitle1">
                                                        {configuration.selectedViolations.map(wc => availableViolations.find(av => av.warningCode === wc)?.description || wc).join(', ') || '-'}
                                                    </Typography>
                                                </TableCell> */}
                                            </TableRow>

                                            {/* Row 2: Is Submit required over AI violation */}
                                            <TableRow>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <Typography component="span" variant="subtitle1" sx={{ lineHeight: 2.5 }}>
                                                        Is Submit required over AI violation?
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <RadioGroup
                                                        row
                                                        name="violation-auto-submit"
                                                        value={configuration.isViolationAutoSubmitEnabled}
                                                        onChange={handleViolationAutoSubmitChange}
                                                    >
                                                        <FormControlLabel
                                                            sx={{ width: 165 }}
                                                            value="Yes"
                                                            control={<Radio disabled={isExamEnabled} />}
                                                            label="Yes"
                                                        />
                                                        <FormControlLabel
                                                            sx={{ width: 165 }}
                                                            value="No"
                                                            control={<Radio disabled={isExamEnabled} />}
                                                            label="No"
                                                        />
                                                    </RadioGroup>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                                    <Typography component="span" variant="subtitle1">
                                                        {configuration.isViolationAutoSubmitEnabled}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>

                                            {/* Row 3: Violation Threshold Count — only when Yes */}
                                            {configuration.isViolationAutoSubmitEnabled === 'Yes' && (
                                                <TableRow>
                                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                                        <Typography component="span" variant="subtitle1" sx={{ lineHeight: 2.5 }}>
                                                            Violation Threshold Count
                                                            <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                                        <TextField
                                                            type="number"
                                                            label="Violation Threshold Count"
                                                            size="small"
                                                            sx={{ maxWidth: 320 }}
                                                            disabled={isExamEnabled}
                                                            value={configuration.violationThresholdCount}
                                                            onChange={handleViolationThresholdCountChange}
                                                            inputProps={{ min: 1 }}
                                                            error={!!violationErrors.violationThresholdCount}
                                                            helperText={violationErrors.violationThresholdCount}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                                        <Typography component="span" variant="subtitle1">
                                                            {configuration.violationThresholdCount}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {/* Row 4: Trigger Violations — only when Yes */}
                                            {configuration.isViolationAutoSubmitEnabled === 'Yes' && (
                                                <TableRow>
                                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                                        <Typography component="span" variant="subtitle1" sx={{ lineHeight: 2.5 }}>
                                                            Trigger Violations
                                                            <Box component="span" sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ verticalAlign: 'top' }}>
                                                        <FormControl sx={{ width: '100%' }}>
                                                            <InputLabel id="triggerViolationsLabel">Trigger Violations</InputLabel>
                                                            <Select
                                                                labelId="triggerViolationsLabel"
                                                                multiple
                                                                sx={{ width: '100%', maxWidth: 320 }}
                                                                disabled={isExamEnabled}
                                                                value={configuration.triggerViolations}
                                                                onChange={handleTriggerViolationsChange}
                                                                input={<OutlinedInput label="Trigger Violations" />}
                                                                renderValue={(selected) => (
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                                        {selected.map((wc) => {
                                                                            const v = availableViolations.find(av => av.warningCode === wc);
                                                                            return (
                                                                                <Box key={wc} sx={{ display: 'flex', gap: 1.5, bgcolor: 'primary.50', py: 0.5, px: 1.5, borderRadius: 1 }}>
                                                                                    <span>{v?.description || wc}</span>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                    </Box>
                                                                )}
                                                            >
                                                                {configuration.selectedViolations.map((wc) => {
                                                                    const v = availableViolations.find(av => av.warningCode === wc);
                                                                    return (
                                                                        <MenuItem key={wc} value={wc}>
                                                                            {v?.description || wc} ({wc})
                                                                        </MenuItem>
                                                                    );
                                                                })}
                                                            </Select>
                                                            {violationErrors.triggerViolations && (
                                                                <FormHelperText sx={{ color: 'error.main' }}>
                                                                    {violationErrors.triggerViolations}
                                                                </FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    </TableCell>
                                                    {/* <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                                        <Typography component="span" variant="subtitle1">
                                                            {configuration.triggerViolations.map(wc => availableViolations.find(av => av.warningCode === wc)?.description || wc).join(', ') || '-'}
                                                        </Typography>
                                                    </TableCell> */}
                                                </TableRow>
                                            )}

                                            {/* Row 5: AI Alert Beep Sound */}
                                            <TableRow>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <Typography component="span" variant="subtitle1" sx={{ lineHeight: 2.5 }}>
                                                        AI Alert Beep Sound
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <RadioGroup
                                                        row
                                                        name="ai-alert-beep"
                                                        value={configuration.aiAlertBeepEnabled}
                                                        onChange={handleAiAlertBeepChange}
                                                    >
                                                        <FormControlLabel
                                                            sx={{ width: 165 }}
                                                            value="Yes"
                                                            control={<Radio disabled={isExamEnabled} />}
                                                            label="Yes"
                                                        />
                                                        <FormControlLabel
                                                            sx={{ width: 165 }}
                                                            value="No"
                                                            control={<Radio disabled={isExamEnabled} />}
                                                            label="No"
                                                        />
                                                    </RadioGroup>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                                    <Typography component="span" variant="subtitle1">
                                                        {configuration.aiAlertBeepEnabled}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}

                                    <TableRow>
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                            <Typography component="span" variant="subtitle1" sx={{ lineHeight: 2.5 }}>
                                                Security Level
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                            <FormControl sx={{ width: '100%' }}>
                                                <Select
                                                    size="small"
                                                    sx={{ width: '100%', maxWidth: 320 }}
                                                    disabled={securitySettingsLocked}
                                                    value={configuration.securityLevel}
                                                    onChange={(e) => {
                                                        const value = e.target.value;
                                                        setConfiguration({
                                                            ...configuration,
                                                            securityLevel: value,
                                                            // NONE: no scope; LOW: exam only; MEDIUM/HIGH: keep the choice, default exam
                                                            dataEncryptionKeyScope: value === 'NONE' ? null : value === 'LOW' ? 'EXAM' : (configuration.dataEncryptionKeyScope || 'EXAM'),
                                                        });
                                                    }}
                                                >
                                                    <MenuItem value="NONE">None</MenuItem>
                                                    <MenuItem value="LOW">Low</MenuItem>
                                                    <MenuItem value="MEDIUM" disabled={configuration.examType !== 'MCQ'}>Medium{configuration.examType !== 'MCQ' ? ' (MCQ only)' : ''}</MenuItem>
                                                    <MenuItem value="HIGH" disabled={configuration.examType !== 'MCQ'}>High{configuration.examType !== 'MCQ' ? ' (MCQ only)' : ''}</MenuItem>
                                                </Select>
                                                {securitySettingsLocked && (
                                                    <FormHelperText>
                                                        Security settings cannot be changed after the configuration is created.
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                            <Typography component="span" variant="subtitle1">
                                                {configuration.securityLevel}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>

                                    {configuration.securityLevel !== 'NONE' && (
                                        <>
                                            <TableRow>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <Typography component="span" variant="subtitle1" sx={{ lineHeight: 2.5 }}>
                                                        Data Encryption Scope
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <RadioGroup
                                                        row
                                                        name="data-encryption-scope"
                                                        value={configuration.dataEncryptionKeyScope}
                                                        onChange={(e, value) => setConfiguration({ ...configuration, dataEncryptionKeyScope: value })}
                                                    >
                                                        <FormControlLabel
                                                            sx={{ width: 165 }}
                                                            value="EXAM"
                                                            control={<Radio disabled={securitySettingsLocked || configuration.securityLevel === 'LOW'} />}
                                                            label="Exam"
                                                        />
                                                        <FormControlLabel
                                                            sx={{ width: 165 }}
                                                            value="QUESTION"
                                                            control={<Radio disabled={securitySettingsLocked || configuration.securityLevel === 'LOW'} />}
                                                            label="Question"
                                                        />
                                                    </RadioGroup>
                                                    <FormHelperText>
                                                        {configuration.securityLevel === 'LOW'
                                                            ? 'Low security supports exam-level encryption only.'
                                                            : configuration.dataEncryptionKeyScope === 'QUESTION'
                                                                ? 'Each question gets its own content encryption key.'
                                                                : 'One content encryption key is used for the whole exam.'}
                                                    </FormHelperText>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                                    <Typography component="span" variant="subtitle1">
                                                        {configuration.dataEncryptionKeyScope}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>

                                            <TableRow>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <Typography component="span" variant="subtitle1" sx={{ lineHeight: 2.5 }}>
                                                        Create Exam Master Key
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top' }}>
                                                    <RadioGroup
                                                        row
                                                        name="create-configuration-kms-key"
                                                        value={configuration.createConfigurationKmsKey}
                                                        onChange={(e, value) => setConfiguration({ ...configuration, createConfigurationKmsKey: value })}
                                                    >
                                                        <FormControlLabel
                                                            sx={{ width: 165 }}
                                                            value="Yes"
                                                            control={<Radio disabled={securitySettingsLocked} />}
                                                            label="Yes"
                                                        />
                                                        <FormControlLabel
                                                            sx={{ width: 165 }}
                                                            value="No"
                                                            control={<Radio disabled={securitySettingsLocked} />}
                                                            label="No"
                                                        />
                                                    </RadioGroup>
                                                    <FormHelperText>
                                                        {configuration.createConfigurationKmsKey === 'Yes'
                                                            ? 'Dedicated KMS keys will be created just for this exam.'
                                                            : "This exam will reuse the client's shared KMS keys."}
                                                            {/* (missing ones are created once) */}
                                                    </FormHelperText>
                                                </TableCell>
                                                <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                                    <Typography component="span" variant="subtitle1">
                                                        {configuration.createConfigurationKmsKey}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </>
                                    )}

                                    <TableRow>
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>

                                                <Typography component='span' variant='subtitle1' sx={{ lineHeight: 2.5 }}>
                                                    Auto logout
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                                {configuration.autoLogout === 'No' && <Typography sx={{ lineHeight: 2.8 }} component='span' variant='subtitle1'>
                                                    Allow Submit How Many Minutes Prior?
                                                </Typography>}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top' }}>
                                            <RadioGroup row name="password-key-for-unlock" onChange={(e, value) => handleConfigChange('autoLogout', value)}>
                                                <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.autoLogout === 'Yes'}
                                                    />
                                                } label="Yes" />
                                                <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.autoLogout === 'No'}
                                                    />
                                                } label="No" />
                                            </RadioGroup>
                                            {configuration.autoLogout === 'No' && <TimeField
                                                className='time-input'
                                                sx={{ mt: 1.5 }}
                                                value={configuration.submitButtonDisplayMinutes}
                                                format="HH:mm"
                                                disabled={isExamEnabled ? true : false}
                                                onChange={(d, e) => handleSubmitButtonDisplayMinutesChange(d, e)}
                                                error={!!errors.submitButtonDisplayMinutes}
                                                slotProps={{
                                                    textField: {
                                                        helperText: errors.submitButtonDisplayMinutes,
                                                    }
                                                }}
                                            />}
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', textAlign: 'right', color: 'primary.main' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.autoLogout}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    {configuration.examType === 'MCQ' && <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Calculator
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <RadioGroup row name="password-key-for-unlock" onChange={(e, value) => handleConfigChange('calculator', value)}>
                                                <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.calculator === 'Yes'}
                                                    />
                                                } label="Yes" />
                                                <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.calculator === 'No'}
                                                    />
                                                } label="No" />
                                            </RadioGroup>
                                            {configuration.calculator === 'Yes' && (
                                                <RadioGroup row name="password-key-for-unlock" onChange={(e, value) => handleConfigChange('calculatorType', value)}>
                                                    <FormControlLabel sx={{ width: 165, textTransform: 'capitalize' }} value='scientific' control={
                                                        <Radio
                                                            disabled={isExamEnabled ? true : false}
                                                            checked={configuration.calculatorType === 'scientific'}
                                                        />
                                                    } label="scientific" />
                                                    <FormControlLabel sx={{ width: 165, textTransform: 'capitalize' }} value='normal' control={
                                                        <Radio
                                                            disabled={isExamEnabled ? true : false}
                                                            checked={configuration.calculatorType === 'normal'}
                                                        />
                                                    } label="normal" />
                                                </RadioGroup>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', color: 'primary.main' }}>
                                            <Typography component='span' variant='subtitle1' sx={{ textTransform: 'capitalize' }}>
                                                {
                                                    configuration.calculator === 'No'
                                                        ? configuration.calculator
                                                        : configuration.calculatorType
                                                }
                                            </Typography>
                                        </TableCell>
                                    </TableRow>}

                                    {configuration.examType === 'DESCRIPTIVE' && <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Provide Graph
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <RadioGroup row name="provide-graph" onChange={(e, value) => handleConfigChange('provideGraph', value)}>
                                                <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.provideGraph === 'Yes'}
                                                    />
                                                } label="Yes" />
                                                <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.provideGraph === 'No'}
                                                    />
                                                } label="No" />
                                            </RadioGroup>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', color: 'primary.main' }}>
                                            <Typography component='span' variant='subtitle1' sx={{ textTransform: 'capitalize' }}>
                                                {configuration.provideGraph}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>}

                                    {configuration.examType !== 'DESCRIPTIVE' && <>
                                        <TableRow>
                                            <TableCell sx={{ borderColor: configuration.feedback === 'No' ? '' : 'transparent !important' }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    Feedback
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderColor: configuration.feedback === 'No' ? '' : 'transparent !important' }}>
                                                <RadioGroup row name="password-key-for-unlock" onChange={(e, value) => handleConfigChange('feedback', value)}>
                                                    <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                        <Radio
                                                            disabled={isExamEnabled ? true : false}
                                                            checked={configuration.feedback === 'Yes'}
                                                        />
                                                    } label="Yes" />
                                                    <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                        <Radio
                                                            disabled={isExamEnabled ? true : false}
                                                            checked={configuration.feedback === 'No'}
                                                        />
                                                    } label="No" />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', color: 'primary.main', borderColor: configuration.feedback === 'No' ? '' : 'transparent !important' }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    {configuration.feedback}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        {configuration.feedback === 'Yes' && (
                                            <TableRow>
                                                <TableCell sx={{ pt: 0 }}>
                                                    <Typography component='span' variant='subtitle1'>
                                                        How many questions
                                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ pt: 0 }}>
                                                    <InputBase
                                                        sx={{
                                                            py: 0.7,
                                                            px: 1,
                                                            width: 180,
                                                            borderRadius: 2,
                                                            border: '1px solid #A3A3A3!important'
                                                        }}
                                                        placeholder="Enter Count"
                                                        type='number'
                                                        disabled={isExamEnabled ? true : false}
                                                        value={configuration.numberOfFeedbackQuestions}
                                                        onChange={handleNumberOfFeedbackQuestionsChange}
                                                        onWheel={(e) => e.target.blur()}
                                                    />
                                                    {errors.numberOfFeedbackQuestions && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                        {errors.numberOfFeedbackQuestions}
                                                    </FormHelperText>}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', color: 'primary.main', pt: 0 }}></TableCell>
                                            </TableRow>
                                        )}
                                    </>}

                                    <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Instructions Required?
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <RadioGroup row name="instructionSheet" onChange={(e, value) => handleConfigChange('instructionSheets', value)}>
                                                <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.instructionSheets === 'Yes'}
                                                    />
                                                } label="Yes" />
                                                <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.instructionSheets === 'No'}
                                                    />
                                                } label="No" />
                                            </RadioGroup>
                                        </TableCell>
                                        <TableCell sx={{
                                            textAlign: 'right',
                                            color: 'primary.main',
                                        }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.instructionSheets}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>


                                    <TableRow>
                                        <TableCell sx={{ borderColor: configuration.biometricRequired === 'Yes' ? 'transparent !important' : '' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                Biometric Required
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ borderColor: configuration.biometricRequired === 'Yes' ? 'transparent !important' : '' }}>
                                            <RadioGroup row name="biometricRequired" onChange={(e, value) => handleConfigChange('biometricRequired', value)}>
                                                <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.biometricRequired === 'Yes'}
                                                    />
                                                } label="Yes" />
                                                <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.biometricRequired === 'No'}
                                                    />
                                                } label="No" />
                                            </RadioGroup>
                                        </TableCell>
                                        <TableCell sx={{
                                            textAlign: 'right',
                                            color: 'primary.main',
                                            borderColor: configuration.biometricRequired === 'Yes' ? 'transparent !important' : ''
                                        }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.biometricRequired}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    {configuration.biometricRequired === 'Yes' && <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Biometric Method
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <FormControl sx={{ width: '100%' }}>
                                                <InputLabel id="biometricMethodLabel">Biometric Methods</InputLabel>
                                                <Select
                                                    labelId="biometricMethodLabel"
                                                    id="biometricMethod"
                                                    multiple
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 320
                                                    }}
                                                    disabled={isExamEnabled ? true : false}
                                                    value={configuration.biometricMethod}
                                                    onChange={handleBiometricMethodChange}
                                                    input={
                                                        <OutlinedInput id="select-multiple-chip" label='Biometric Methods' />
                                                    }
                                                    renderValue={(selected) => <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                        {selected.map((value) => (
                                                            <Box key={value} sx={{
                                                                display: 'flex',
                                                                gap: 1.5,
                                                                bgcolor: 'primary.50',
                                                                py: 0.5,
                                                                px: 1.5,
                                                                borderRadius: 1,
                                                                textTransform: 'capitalize',
                                                            }}>
                                                                <span>{
                                                                    value === 'PHOTO' ? 'Photo'
                                                                        : value === 'THUMB_IMPRESSION' ? 'Thumb Impression'
                                                                            : value === 'IRIS_SCAN' ? 'Iris Scan'
                                                                                : value
                                                                }</span>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                    }
                                                >
                                                    <MenuItem value='PHOTO'>Photo</MenuItem>
                                                    <MenuItem value='THUMB_IMPRESSION'>Thumb Impression</MenuItem>
                                                    <MenuItem value='IRIS_SCAN'>Iris Scan</MenuItem>
                                                </Select>
                                                {biometricMethodError && (
                                                    <FormHelperText sx={{ color: 'error.main' }}>
                                                        Please select at least one biometric method
                                                    </FormHelperText>
                                                )}
                                            </FormControl>
                                        </TableCell>
                                        <TableCell sx={{
                                            textAlign: 'right',
                                            color: 'primary.main',
                                        }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.biometricMethod.map((method) => {
                                                    if (method === 'PHOTO') {
                                                        return 'Photo';
                                                    } else if (method === 'THUMB_IMPRESSION') {
                                                        return 'Thumb Impression';
                                                    } else if (method === 'IRIS_SCAN') {
                                                        return 'Iris Scan';
                                                    } else {
                                                        return method;
                                                    }
                                                }).join(', ')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>}
                                    <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Sample Test Required
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <RadioGroup row name="instructionSheet" onChange={(e, value) => handleConfigChange('sampleTestRequired', value)}>
                                                <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.sampleTestRequired === 'Yes'}
                                                    />
                                                } label="Yes" />
                                                <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                    <Radio
                                                        disabled={isExamEnabled ? true : false}
                                                        checked={configuration.sampleTestRequired === 'No'}
                                                    />
                                                } label="No" />
                                            </RadioGroup>
                                        </TableCell>
                                        <TableCell sx={{
                                            textAlign: 'right',
                                            color: 'primary.main',
                                        }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.sampleTestRequired}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>

                                    {configuration.examType !== 'DESCRIPTIVE' && <>
                                        <TableRow>
                                            <TableCell sx={{ borderColor: configuration.maximumLoginAttempts === 'Yes' ? 'transparent !important' : '' }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    Maximum Login Attempts
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderColor: configuration.maximumLoginAttempts === 'Yes' ? 'transparent !important' : '' }}>
                                                <RadioGroup row name="maximumLoginAttempts" onChange={(e, value) => handleMaximumLoginAttemptsChange(value)}>
                                                    <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                        <Radio
                                                            disabled={canEditNow ? false : (isExamEnabled ? true : false)}
                                                            checked={configuration.maximumLoginAttempts === 'Yes'}
                                                        />
                                                    } label="Yes" />
                                                    <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                        <Radio
                                                            disabled={canEditNow ? false : (isExamEnabled ? true : false)}
                                                            checked={configuration.maximumLoginAttempts === 'No'}
                                                        />
                                                    } label="No" />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{
                                                textAlign: 'right',
                                                color: 'primary.main',
                                                borderColor: configuration.maximumLoginAttempts === 'Yes' ? 'transparent !important' : ''
                                            }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    {configuration.maximumLoginAttempts}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        {configuration.maximumLoginAttempts === 'Yes' && (
                                            <TableRow>
                                                <TableCell>
                                                    <Typography component='span' variant='subtitle1'>
                                                        Maximum Login Attempts Count
                                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <InputBase
                                                        sx={{
                                                            py: 0.7,
                                                            px: 1,
                                                            width: 180,
                                                            borderRadius: 2,
                                                            border: '1px solid'
                                                        }}
                                                        placeholder="Enter Count"
                                                        type='number'
                                                        value={configuration.maximumLoginAttemptsCount}
                                                        disabled={canEditNow ? false : isExamEnabled}
                                                        onChange={handleMaximumLoginAttemptsCountChange}
                                                        onWheel={(e) => e.target.blur()}
                                                    />
                                                    {errors.maximumLoginAttemptsCount && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                        {errors.maximumLoginAttemptsCount}
                                                    </FormHelperText>}
                                                </TableCell>
                                                <TableCell sx={{
                                                    textAlign: 'right',
                                                    color: 'primary.main'
                                                }}>
                                                    <Typography component='span' variant='subtitle1'>
                                                        {configuration.maximumLoginAttemptsCount} Login Attempts
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </>}


                                    <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Instructions Reading Time
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <TimeField
                                                className='time-input'
                                                defaultValue={configuration.loginBeforeMinutes}
                                                value={configuration.loginBeforeMinutes}
                                                format="HH:mm"
                                                disabled={configuration.startExamBetweenSpecifiedTime ? true : (canEditNow ? false : isExamEnabled)}
                                                error={!!errors.loginBeforeMinutes}
                                                onChange={handleLoginBeforeMinutesChange}
                                                slotProps={{
                                                    textField: {
                                                        helperText: errors.loginBeforeMinutes,
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', color: 'primary.main' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {`${configuration.loginBeforeMinutes.format('HH')} Hours ${configuration.loginBeforeMinutes.format('mm')} Minutes`}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ borderColor: configuration.lateLoginAllowed === 'Yes' ? 'transparent !important' : '' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                Late Login Allowed
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ borderColor: configuration.lateLoginAllowed === 'Yes' ? 'transparent !important' : '' }}>
                                            <RadioGroup row name="lateLoginAllowed" onChange={(e, value) => handleConfigChange('lateLoginAllowed', value)}>
                                                <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                    <Radio
                                                        disabled={configuration.startExamBetweenSpecifiedTime ? true : (canEditNow ? false : isExamEnabled)}
                                                        checked={configuration.lateLoginAllowed === 'Yes'}
                                                    />
                                                } label="Yes" />
                                                <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                    <Radio
                                                        disabled={configuration.startExamBetweenSpecifiedTime ? true : (canEditNow ? false : isExamEnabled)}
                                                        checked={configuration.lateLoginAllowed === 'No'}
                                                    />
                                                } label="No" />
                                            </RadioGroup>
                                        </TableCell>
                                        <TableCell sx={{
                                            textAlign: 'right',
                                            color: 'primary.main',
                                            borderColor: configuration.lateLoginAllowed === 'Yes' ? 'transparent !important' : ''
                                        }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.lateLoginAllowed}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    {configuration.lateLoginAllowed === 'Yes' && <TableRow>
                                        <TableCell sx={{ pt: 0 }}>
                                            <Typography component='span' variant='subtitle1'>
                                                Till how many minutes after the exam starts
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ pt: 0 }}>
                                            <TimeField
                                                className='time-input'
                                                value={configuration.lateLoginMinutes}
                                                format="HH:mm"
                                                disabled={configuration.startExamBetweenSpecifiedTime ? true : (canEditNow ? false : isExamEnabled)}
                                                error={!!errors.lateLoginMinutes}
                                                onChange={handleLateLoginMinuteChange}
                                                slotProps={{
                                                    textField: {
                                                        helperText: errors.lateLoginMinutes,
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{ pt: 0, textAlign: 'right', color: 'primary.main' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {`${configuration.lateLoginMinutes.format('HH')} Hours ${configuration.lateLoginMinutes.format('mm')} Minutes`}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>}
                                    <TableRow>
                                        <TableCell sx={{ borderColor: configuration.reminders.length ? 'transparent !important' : '' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                How Many Reminders For Exam Remaining Time
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ borderColor: configuration.reminders.length ? 'transparent !important' : '' }}>
                                            <Paper sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                width: 180,
                                                borderRadius: 2,
                                                boxShadow: 'none',
                                                border: '1px solid #A3A3A3!important'
                                            }}>
                                                <InputBase
                                                    sx={{ ml: 1, flex: 1 }}
                                                    placeholder="Enter Count"
                                                    type='number'
                                                    value={remindersCount}
                                                    disabled={isExamEnabled ? true : false}
                                                    onChange={handleReminderCountChange}
                                                    onWheel={(e) => e.target.blur()}
                                                />
                                                <Divider sx={{ height: 44, borderColor: '#A3A3A3' }} orientation="vertical" />
                                                <IconButton color="primary" onClick={() => {
                                                    handleReminderInputCheck()
                                                }} disableRipple>
                                                    <img src={TRUE_BLACK} alt='' />
                                                </IconButton>
                                            </Paper>
                                            {errors.remindersCount && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                {errors.remindersCount}
                                            </FormHelperText>}
                                        </TableCell>
                                        <TableCell sx={{
                                            textAlign: 'right',
                                            color: 'primary.main',
                                            borderColor: configuration.reminders.length ? 'transparent !important' : ''
                                        }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.reminders.length ? 'Yes' : 'No'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    {configuration.reminders.map((reminder, index) => <TableRow key={index}>
                                        <TableCell sx={{
                                            pt: 0,
                                            borderColor: index === configuration.reminders.length - 1 ? '' : 'transparent !important'
                                        }}>
                                        </TableCell>
                                        <TableCell sx={{
                                            pt: 0,
                                            borderColor: index === configuration.reminders.length - 1 ? '' : 'transparent !important'
                                        }}>
                                            <TimeField
                                                className='time-input'
                                                defaultValue={reminder}
                                                value={reminder}
                                                format="HH:mm"
                                                disabled={isExamEnabled ? true : false}
                                                onChange={(d, e) => handleRemindersTimeChange(d, e, index)}
                                                error={!!errors.reminders[index]}
                                                slotProps={{
                                                    textField: {
                                                        helperText: errors.reminders[index],
                                                    }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell sx={{
                                            textAlign: 'right',
                                            color: 'primary.main',
                                            pt: 0,
                                            borderColor: index === configuration.reminders.length - 1 ? '' : 'transparent !important'
                                        }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {`${reminder?.format('HH')} Hours ${reminder?.format('mm')} Minutes`}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    )}

                                    <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Forced Logout
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <RadioGroup row name="no-movement"
                                                onChange={(e, value) => handleConfigChange('forcedLogout', value)}>
                                                <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                    <Radio
                                                        disabled={isExamEnabled || configuration.startExamBetweenSpecifiedTime ? true : false}
                                                        checked={configuration.forcedLogout === 'Yes'}
                                                    />
                                                } label="Yes" />
                                                <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                    <Radio
                                                        disabled={isExamEnabled || configuration.startExamBetweenSpecifiedTime ? true : false}
                                                        checked={configuration.forcedLogout === 'No'}
                                                    />
                                                } label="No" />
                                            </RadioGroup>
                                        </TableCell>
                                        <TableCell sx={{
                                            textAlign: 'right',
                                            color: 'primary.main'
                                        }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.forcedLogout}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>

                                    {configuration.examType === 'MCQ' && <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Upload Log Tables
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            overflow: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}>
                                            <RadiusButton
                                                variant='contained'
                                                component='label'
                                                htmlFor='logTablesFile'
                                                color='primary'
                                                disabled={isExamEnabled}
                                            >
                                                {documentFiles?.logTablesFile || documentFiles.signedLogTablesFile
                                                    ? 'Change File'
                                                    : 'Select File'}
                                            </RadiusButton>
                                            <input
                                                style={{ display: 'none' }}
                                                type='file'
                                                id='logTablesFile'
                                                onChange={(e) => handleFileChange(e.target, 'logTablesFile')}
                                                accept={mimes.CONFIGURATION_DOCUMENT_ALLOWED_TYPES.join(',')}
                                            />
                                            {
                                                documentFiles.logTablesFile?.name && <>
                                                    <Typography variant='subtitle1' sx={{ maxWidth: 270, wordBreak: 'break-all' }}>
                                                        {documentFiles.logTablesFile.name}
                                                    </Typography>
                                                    <img
                                                        style={{ cursor: 'pointer' }}
                                                        src={DELETE_ICON}
                                                        onClick={() => handleFileRemove('logTablesFile')}
                                                    />
                                                </>
                                            }
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            {documentFiles?.signedLogTablesFile && <LoadingButton
                                                sx={{
                                                    borderRadius: '46px',
                                                    textTransform: 'none',
                                                    mr: 1
                                                }}
                                                variant='outlined'
                                                disabled={isExamEnabled}
                                                color='error'
                                                onClick={() => handleRemoveLogTables()}
                                            >
                                                Delete
                                            </LoadingButton>}
                                            {documentFiles?.signedLogTablesFile && <LoadingButton
                                                sx={{
                                                    borderRadius: '46px',
                                                    textTransform: 'none'
                                                }}
                                                variant='outlined'
                                                onClick={() => handleDownloadFile('signedLogTablesFile')}
                                                loadingPosition="start"
                                                startIcon={<img src={ACTION_DOWNLOAD} alt='' />}
                                            >
                                                Download
                                            </LoadingButton>}
                                        </TableCell>
                                    </TableRow>}

                                    {configuration.examType === 'MCQ' && <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Upload PV Charts
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            overflow: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}>
                                            <RadiusButton
                                                variant='contained'
                                                component='label'
                                                htmlFor='pvChartsFile'
                                                color='primary'
                                                disabled={isExamEnabled}
                                            >
                                                {documentFiles?.pvChartsFile || documentFiles.signedPvChartsFile
                                                    ? 'Change File'
                                                    : 'Select File'}
                                            </RadiusButton>
                                            <input
                                                style={{ display: 'none' }}
                                                type='file'
                                                id='pvChartsFile'
                                                onChange={(e) => handleFileChange(e.target, 'pvChartsFile')}
                                                accept={mimes.CONFIGURATION_DOCUMENT_ALLOWED_TYPES.join(',')}
                                            />
                                            {
                                                documentFiles.pvChartsFile?.name && <>
                                                    <Typography variant='subtitle1' sx={{ maxWidth: 270, wordBreak: 'break-all' }}>
                                                        {documentFiles.pvChartsFile.name}
                                                    </Typography>
                                                    <img
                                                        style={{ cursor: 'pointer' }}
                                                        src={DELETE_ICON}
                                                        onClick={() => handleFileRemove('pvChartsFile')}
                                                    />
                                                </>
                                            }
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            {documentFiles?.signedPvChartsFile && <LoadingButton
                                                sx={{
                                                    borderRadius: '46px',
                                                    textTransform: 'none',
                                                    mr: 1
                                                }}
                                                variant='outlined'
                                                disabled={isExamEnabled}
                                                color='error'
                                                onClick={() => handleRemovePvCharts()}
                                            >
                                                Delete
                                            </LoadingButton>}
                                            {documentFiles?.signedPvChartsFile && <LoadingButton
                                                sx={{
                                                    borderRadius: '46px',
                                                    textTransform: 'none'
                                                }}
                                                variant='outlined'
                                                onClick={() => handleDownloadFile('signedPvChartsFile')}
                                                loadingPosition="start"
                                                startIcon={<img src={ACTION_DOWNLOAD} alt='' />}
                                            >
                                                Download
                                            </LoadingButton>}
                                        </TableCell>
                                    </TableRow>}

                                    {configuration.examType === 'MCQ' && <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Upload Heat Table
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            overflow: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}>
                                            <RadiusButton
                                                variant='contained'
                                                component='label'
                                                htmlFor='heatTableFile'
                                                color='primary'
                                                disabled={isExamEnabled}
                                            >
                                                {documentFiles?.heatTableFile || documentFiles.signedHeatTableFile
                                                    ? 'Change File'
                                                    : 'Select File'}
                                            </RadiusButton>
                                            <input
                                                style={{ display: 'none' }}
                                                type='file'
                                                id='heatTableFile'
                                                onChange={(e) => handleFileChange(e.target, 'heatTableFile')}
                                                accept={mimes.CONFIGURATION_DOCUMENT_ALLOWED_TYPES.join(',')}
                                            />
                                            {
                                                documentFiles.heatTableFile?.name && <>
                                                    <Typography variant='subtitle1' sx={{ maxWidth: 270, wordBreak: 'break-all' }}>
                                                        {documentFiles.heatTableFile.name}
                                                    </Typography>
                                                    <img
                                                        style={{ cursor: 'pointer' }}
                                                        src={DELETE_ICON}
                                                        onClick={() => handleFileRemove('heatTableFile')}
                                                    />
                                                </>
                                            }
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            {documentFiles?.signedHeatTableFile && <LoadingButton
                                                sx={{
                                                    borderRadius: '46px',
                                                    textTransform: 'none',
                                                    mr: 1
                                                }}
                                                variant='outlined'
                                                disabled={isExamEnabled}
                                                color='error'
                                                onClick={() => handleRemoveHeatTable()}
                                            >
                                                Delete
                                            </LoadingButton>}
                                            {documentFiles?.signedHeatTableFile && <LoadingButton
                                                sx={{
                                                    borderRadius: '46px',
                                                    textTransform: 'none'
                                                }}
                                                variant='outlined'
                                                onClick={() => handleDownloadFile('signedHeatTableFile')}
                                                loadingPosition="start"
                                                startIcon={<img src={ACTION_DOWNLOAD} alt='' />}
                                            >
                                                Download
                                            </LoadingButton>}
                                        </TableCell>
                                    </TableRow>}

                                    {configuration.examType === 'MCQ' && <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Upload Help Document
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            overflow: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}>
                                            <RadiusButton
                                                variant='contained'
                                                component='label'
                                                htmlFor='helpDocumentFile'
                                                color='primary'
                                                disabled={isExamEnabled}
                                            >
                                                {documentFiles?.helpDocumentFile || documentFiles.signedHelpDocumentFile
                                                    ? 'Change File'
                                                    : 'Select File'}
                                            </RadiusButton>
                                            <input
                                                style={{ display: 'none' }}
                                                type='file'
                                                id='helpDocumentFile'
                                                onChange={(e) => handleFileChange(e.target, 'helpDocumentFile')}
                                                accept={mimes.CONFIGURATION_DOCUMENT_ALLOWED_TYPES.join(',')}
                                            />
                                            {
                                                documentFiles.helpDocumentFile?.name && <>
                                                    <Typography variant='subtitle1' sx={{ maxWidth: 270, wordBreak: 'break-all' }}>
                                                        {documentFiles.helpDocumentFile.name}
                                                    </Typography>
                                                    <img
                                                        style={{ cursor: 'pointer' }}
                                                        src={DELETE_ICON}
                                                        onClick={() => handleFileRemove('helpDocumentFile')}
                                                    />
                                                </>
                                            }
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right' }}>
                                            {documentFiles?.signedHelpDocumentFile && <LoadingButton
                                                sx={{
                                                    borderRadius: '46px',
                                                    textTransform: 'none',
                                                    mr: 1
                                                }}
                                                variant='outlined'
                                                color='error'
                                                disabled={isExamEnabled}
                                                onClick={() => handleRemoveHelpDocument()}
                                            >
                                                Delete
                                            </LoadingButton>}
                                            {documentFiles?.signedHelpDocumentFile && <LoadingButton
                                                sx={{
                                                    borderRadius: '46px',
                                                    textTransform: 'none'
                                                }}
                                                variant='outlined'
                                                onClick={() => handleDownloadFile('signedHelpDocumentFile')}
                                                loadingPosition="start"
                                                startIcon={<img src={ACTION_DOWNLOAD} alt='' />}
                                            >
                                                Download
                                            </LoadingButton>}
                                        </TableCell>
                                    </TableRow>}

                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CustomTabPanel>
                    <CustomTabPanel value={tabValue} index={1}>
                        <TableContainer sx={{
                            p: 2,
                            pt: 0.25,
                            maxHeight: 'inherit',
                            maxWidth: 'inherit',
                            // overflow: 'auto',
                            borderRadius: 1
                        }}>
                            <Table stickyHeader aria-label="table">
                                <TableBody>
                                    {configuration.examType === 'MCQ'
                                        ? <>
                                            <TableRow>
                                                <TableCell sx={{
                                                    borderColor: configuration.sectionWise === 'Yes' ? 'transparent !important' : ''
                                                }}>
                                                    <Typography component='span' variant='subtitle1'>
                                                        Does The Exam Have Multiple Sections?
                                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{
                                                    borderColor:
                                                        configuration.sectionWise === 'Yes' ? 'transparent !important' : ''
                                                }}>
                                                    <RadioGroup row name="password-key-for-unlock" onChange={(e, value) => handleSectionWiseChange(value)}>
                                                        <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                            <Radio
                                                                disabled={contentUploaded || isExamEnabled || isLocked ? true : false}
                                                                checked={configuration.sectionWise === 'Yes'}
                                                            />
                                                        } label="Yes" />
                                                        <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                            <Radio
                                                                disabled={contentUploaded || isExamEnabled || isLocked ? true : false}
                                                                checked={configuration.sectionWise === 'No'}
                                                            />
                                                        } label="No" />
                                                    </RadioGroup>
                                                </TableCell>
                                                <TableCell sx={{
                                                    textAlign: 'right',
                                                    color: 'primary.main',
                                                    borderColor: configuration.sectionWise === 'Yes' ? 'transparent !important' : ''
                                                }}>
                                                    <Typography component='span' variant='subtitle1'>
                                                        {configuration.sectionWise}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <Typography component='span' variant='subtitle1'>
                                                        Question Selection Type
                                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <RadioGroup row name="question-selection-type" value={configuration.selectionType} onChange={(e, value) => handleSelectionTypeChange(e.target.value)}>
                                                        <FormControlLabel sx={{ width: 165 }} value='fixed' control={
                                                            <Radio
                                                                disabled={contentUploaded || isExamEnabled || isLocked ? true : false}
                                                                checked={configuration.selectionType === 'fixed'}
                                                            />
                                                        } label="Fixed" />
                                                        <FormControlLabel sx={{ width: 165 }} value='random' control={
                                                            <Radio
                                                                disabled={contentUploaded || isExamEnabled || isLocked ? true : false}
                                                                checked={configuration.selectionType === 'random'}
                                                            />
                                                        } label="Random" />
                                                    </RadioGroup>
                                                </TableCell>
                                                <TableCell sx={{
                                                    textAlign: 'right',
                                                    color: 'primary.main',
                                                }}>
                                                    <Typography component='span' variant='subtitle1'>
                                                        {configuration.selectionType === 'fixed' ? 'Fixed' : 'Random'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            {configuration.sectionWise === 'Yes' && <TableRow>
                                                <TableCell sx={{ border: 'none' }}>
                                                    <Typography component='span' variant='subtitle1'>
                                                        Section Navigation
                                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ border: 'none' }}>
                                                    <FormControl sx={{ width: '100%' }}>
                                                        <InputLabel id="sectionNavigationLabel">Section Navigation</InputLabel>
                                                        <Select
                                                            id="examType"
                                                            labelId="sectionNavigationLabel"
                                                            label='Section Navigation'
                                                            sx={{
                                                                width: '100%',
                                                                maxWidth: 320,
                                                                textTransform: 'capitalize',
                                                            }}
                                                            disabled={isExamEnabled || contentUploaded || isLocked ? true : false}
                                                            value={configuration.sectionNavigation}
                                                            onChange={handleSectionNavigationChange}
                                                            input={
                                                                <OutlinedInput label='Section Navigation' />
                                                            }
                                                        >
                                                            <MenuItem value='NO_MOVEMENT_OR_TIME_RESTRICTION'>No Movement Or Time Restriction</MenuItem>
                                                            <MenuItem value='FIXED_TIME_AND_MOVEMENT_RESTRICTION'>Fixed Time And Movement Restriction</MenuItem>
                                                            <MenuItem value='MOVEMENT_RESTRICTION_WITH_INCREMENTAL_TIME'>Movement Restriction With Incremental Time</MenuItem>
                                                            <MenuItem value='MOVEMENT_RESTRICTION_WITH_NO_TIME'>Movement Restriction With No Time</MenuItem>
                                                        </Select>
                                                    </FormControl>
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', color: 'primary.main', border: 'none' }}>
                                                    <Typography component='span' variant='subtitle1'>
                                                        {
                                                            configuration.sectionNavigation === 'NO_MOVEMENT_OR_TIME_RESTRICTION'
                                                                ? 'No Movement Or Time Restriction'
                                                                : configuration.sectionNavigation === 'FIXED_TIME_AND_MOVEMENT_RESTRICTION'
                                                                    ? 'Fixed Time And Movement Restriction'
                                                                    : configuration.sectionNavigation === 'MOVEMENT_RESTRICTION_WITH_INCREMENTAL_TIME'
                                                                        ? 'Movement Restriction With Incremental Time'
                                                                        : 'Movement Restriction With No Time'
                                                        }
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>}
                                            {configuration.sectionWise === 'Yes' && (
                                                <TableRow>
                                                    <TableCell sx={{ border: 'none' }}>
                                                        <Typography component='span' variant='subtitle1'>
                                                            How many sections
                                                            <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ border: 'none' }}>
                                                        <Paper sx={{
                                                            display: 'flex', alignItems: 'center', width: 180, borderRadius: 2, boxShadow: 'none', border: '1px solid #A3A3A3!important'
                                                        }}>
                                                            <InputBase
                                                                sx={{ ml: 1, flex: 1 }}
                                                                placeholder="Enter Count"
                                                                type='number'
                                                                value={sectionCount}
                                                                disabled={isExamEnabled || isLocked ? true : false}
                                                                onChange={handleCountChange}
                                                                onWheel={(e) => e.target.blur()}
                                                            />
                                                            <Divider sx={{ height: 44, borderColor: '#A3A3A3' }} orientation="vertical" />
                                                            <IconButton type='button' color="primary" onClick={() => {
                                                                if (isLocked) return;
                                                                if (errors.sectionCount) return;
                                                                handleSectionInputCheck()
                                                            }} disableRipple>
                                                                <img src={TRUE_BLACK} alt='' />
                                                            </IconButton>
                                                        </Paper>
                                                        {errors.sectionCount && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                            {errors.sectionCount}
                                                        </FormHelperText>}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'right', color: 'primary.main', border: 'none' }}>
                                                        <Typography component='span' variant='subtitle1'>
                                                            {configuration.sectionWise}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                            {configuration.sectionWise === 'Yes' && (
                                                configuration.sections.map((section, index) => <TableRow key={index}>
                                                    <TableCell sx={{
                                                        pt: 0,
                                                        borderColor: index === configuration.sections.length - 1 ? '' : 'transparent !important',
                                                        verticalAlign: 'top !important'
                                                    }}>
                                                        <Typography component='span' variant='subtitle1'>
                                                            {`Section ${index + 1}`}
                                                            <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        pt: 0,
                                                        borderColor: index === configuration.sections.length - 1 ? '' : 'transparent !important'
                                                    }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-start',
                                                            gap: 1,
                                                        }}>
                                                            <TextField
                                                                sx={{ width: 220 }}
                                                                size='small'
                                                                label="Section Title"
                                                                variant="outlined"
                                                                value={configuration.sections[index].sectionTitle}
                                                                disabled={isExamEnabled || isLocked ? true : false}
                                                                onChange={(e) => handleSectionTitleChange(e, index)}
                                                            />
                                                            <TextField
                                                                sx={{ width: 220 }}
                                                                size='small'
                                                                label="No. of Questions"
                                                                variant="outlined"
                                                                value={configuration.sections[index].noOfQuestions}
                                                                disabled={isExamEnabled || isLocked ? true : false}
                                                                onChange={(e) => handleSectionNoOfQuestionsChange(e, index)}
                                                            />
                                                            {(configuration.sectionNavigation === 'FIXED_TIME_AND_MOVEMENT_RESTRICTION' || configuration.sectionNavigation === 'MOVEMENT_RESTRICTION_WITH_INCREMENTAL_TIME') && <TimeField
                                                                size='small'
                                                                sx={{ width: 80 }}
                                                                defaultValue={configuration.sections[index].sectionTime}
                                                                value={configuration.sections[index].sectionTime}
                                                                format="HH:mm"
                                                                disabled={isExamEnabled || isLocked ? true : false}
                                                                onChange={(d, e) => handleSectionTimeChange(d, e, index)}
                                                            />}

                                                        </Box>
                                                        {
                                                            <>
                                                                {errors.sections[index]?.titleError && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                                    {errors.sections[index]?.titleError}
                                                                </FormHelperText>}
                                                                {errors.sections[index]?.timeError && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                                    {errors.sections[index]?.timeError}
                                                                </FormHelperText>}
                                                            </>
                                                        }

                                                        <Box sx={{ display: 'flex', flexDirection: 'column', pl: 2 }}>

                                                            <InputLabel sx={{ mt: 1, fontSize: 13 }}>How many sub sections</InputLabel>
                                                            <Paper sx={{
                                                                my: 1,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                width: 180,
                                                                height: 32,
                                                                borderRadius: 2,
                                                                gap: 1,
                                                                boxShadow: 'none',
                                                                border: '1px solid #A3A3A3!important'
                                                            }}>
                                                                <InputBase
                                                                    sx={{ ml: 1, flex: 1, p: 0 }}
                                                                    placeholder="Enter Count"
                                                                    type='number'
                                                                    value={section.subSectionsCount}
                                                                    disabled={isExamEnabled || isLocked ? true : false}
                                                                    onChange={(e) => { handleSubSectionsCountChange(index, e.target.value) }}
                                                                    onWheel={(e) => e.target.blur()}
                                                                />
                                                                <Divider sx={{ height: '100%', borderColor: '#A3A3A3' }} orientation="vertical" />
                                                                <IconButton color="primary" onClick={(e) => { handleSubSectionInputCheck(index, e.target.value) }} disableRipple>
                                                                    <img src={TRUE_BLACK} alt='' />
                                                                </IconButton>
                                                            </Paper>
                                                            {errors.sections[index]?.countError && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                                {errors.sections[index].countError}
                                                            </FormHelperText>}
                                                            {section.subSections.map((subSection, subIndex) => <Box key={subIndex}>
                                                                <TextField
                                                                    sx={{ width: 220, mt: 1.5 }}
                                                                    size='small'
                                                                    label={`Sub Section ${subIndex + 1}`}
                                                                    variant="outlined"
                                                                    value={
                                                                        typeof subSection === 'string'
                                                                            ? subSection
                                                                            : subSection.title
                                                                    }
                                                                    disabled={isExamEnabled || isLocked ? true : false}
                                                                    onChange={(e) => handleSubSectionTitleChange(index, subIndex, e.target.value)}
                                                                    InputLabelProps={{
                                                                        shrink: true
                                                                    }}
                                                                />
                                                                <TextField
                                                                    sx={{ width: 220, mt: 1.5, ml: 1 }}
                                                                    size='small'
                                                                    label={`No of Questions`}
                                                                    variant="outlined"
                                                                    value={
                                                                        typeof subSection === 'string'
                                                                            ? ''
                                                                            : subSection.noOfQuestions ?? ''
                                                                    }
                                                                    disabled={isExamEnabled || isLocked ? true : false}
                                                                    onChange={(e) => handleSubSectionNoOfQuestionsChange(index, subIndex, e.target.value)}
                                                                    InputLabelProps={{
                                                                        shrink: true
                                                                    }}
                                                                />

                                                                {errors.sections[index]?.subSections[subIndex] && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                                    {errors.sections[index]?.subSections[subIndex]}
                                                                </FormHelperText>}
                                                            </Box>)}
                                                        </Box>
                                                        {
                                                            (configuration.sections.length - 1) === index && errors.sectionError && (
                                                                <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                                    {errors.sectionError}
                                                                </FormHelperText>
                                                            )
                                                        }
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        textAlign: 'right',
                                                        color: 'primary.main',
                                                        pt: 0,
                                                        borderColor: index === configuration.sections.length - 1 ? '' : 'transparent !important',
                                                        verticalAlign: 'top !important'
                                                    }}>
                                                        <Typography component='span' variant='subtitle1'>
                                                            {`${configuration.sections?.[index]?.sectionTime?.format('HH')} Hours ${configuration.sections?.[index]?.sectionTime?.format('mm')} Minutes`}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>)
                                            )}

                                            {configuration.examType === 'MCQ'
                                                ? <TableRow>
                                                    <TableCell>
                                                        <Typography component='span' variant='subtitle1'>
                                                            Number of Questions Per Set
                                                            <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <InputBase
                                                            sx={{
                                                                py: 0.7,
                                                                px: 1,
                                                                width: 180,
                                                                borderRadius: 2,
                                                                border: '1px solid'
                                                            }}
                                                            placeholder="Enter Count"
                                                            type='number'
                                                            value={configuration.questionsPerSet}
                                                            disabled={isExamEnabled || contentUploaded || isLocked ? true : false}
                                                            onChange={handleQuestionsPerSetChange}
                                                            onWheel={(e) => e.target.blur()}
                                                        />
                                                        {errors?.lowQuestions && (
                                                            <FormHelperText sx={{ color: 'error.main' }}>
                                                                {errors.lowQuestions}
                                                            </FormHelperText>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        textAlign: 'right',
                                                        color: 'primary.main'
                                                    }}>
                                                        <Typography component='span' variant='subtitle1'>
                                                            {configuration.questionsPerSet} Questions
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                                : null
                                            }

                                            <TableRow>
                                                <TableCell sx={{ borderColor: configuration.contentUploadMethod === 'bulk' ? 'transparent !important' : null }}>
                                                    <Typography component='span' variant='subtitle1'>
                                                        Content Upload Method
                                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ borderColor: configuration.contentUploadMethod === 'bulk' ? 'transparent !important' : null }}>
                                                    <RadioGroup row name="instructionSheet" onChange={handleContentUploadMethodChange}>
                                                        <FormControlLabel sx={{ width: 165 }} value='set' control={
                                                            <Radio
                                                                disabled={isExamEnabled || contentUploaded || isLocked ? true : false}
                                                                checked={configuration.contentUploadMethod === 'set'}
                                                            />
                                                        } label="Setwise Upload" />
                                                        <FormControlLabel sx={{ width: 165 }} value='bulk' control={
                                                            <Radio
                                                                disabled={configuration.sectionWise === 'Yes' || isExamEnabled || contentUploaded || isLocked ? true : false}
                                                                checked={configuration.contentUploadMethod === 'bulk'}
                                                            />
                                                        } label="Bulk upload" />
                                                    </RadioGroup>
                                                </TableCell>
                                                <TableCell sx={{
                                                    textAlign: 'right',
                                                    color: 'primary.main',
                                                    borderColor: configuration.contentUploadMethod === 'bulk' ? 'transparent !important' : null
                                                }}>
                                                    <Typography component='span' variant='subtitle1'>
                                                        {configuration.contentUploadMethod === 'set' ? 'Setwise upload' : 'Bulk upload'}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            {configuration.contentUploadMethod === 'bulk' && <>
                                                <TableRow>
                                                    <TableCell sx={{ borderColor: 'transparent !important' }}>
                                                        <Typography component='span' variant='subtitle1'>
                                                            Number of easy questions
                                                            <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ borderColor: 'transparent !important' }}>
                                                        <InputBase
                                                            sx={{
                                                                py: 0.7,
                                                                px: 1,
                                                                width: 180,
                                                                borderRadius: 2,
                                                                border: '1px solid #A3A3A3!important'
                                                            }}
                                                            placeholder="Enter Count"
                                                            type='number'
                                                            disabled={isExamEnabled || isLocked ? true : false}
                                                            value={configuration.numberOfEasyQuestions}
                                                            onChange={(e) => handleNumberOfQuestionsChange(e, 'numberOfEasyQuestions')}
                                                            onWheel={(e) => e.target.blur()}
                                                        />
                                                        {errors.numberOfEasyQuestions && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                            {errors.numberOfEasyQuestions}
                                                        </FormHelperText>}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        borderColor: 'transparent !important'
                                                    }}></TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ borderColor: 'transparent !important' }}>
                                                        <Typography component='span' variant='subtitle1'>
                                                            Number of medium questions
                                                            <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ borderColor: 'transparent !important' }}>
                                                        <InputBase
                                                            sx={{
                                                                py: 0.7,
                                                                px: 1,
                                                                width: 180,
                                                                borderRadius: 2,
                                                                border: '1px solid #A3A3A3!important'
                                                            }}
                                                            placeholder="Enter Count"
                                                            type='number'
                                                            disabled={isExamEnabled || isLocked ? true : false}
                                                            value={configuration.numberOfMediumQuestions}
                                                            onChange={(e) => handleNumberOfQuestionsChange(e, 'numberOfMediumQuestions')}
                                                            onWheel={(e) => e.target.blur()}
                                                        />
                                                        {errors.numberOfMediumQuestions && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                            {errors.numberOfMediumQuestions}
                                                        </FormHelperText>}
                                                    </TableCell>
                                                    <TableCell sx={{
                                                        borderColor: 'transparent !important'
                                                    }}></TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell>
                                                        <Typography component='span' variant='subtitle1'>
                                                            Number of hard questions
                                                            <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <InputBase
                                                            sx={{
                                                                py: 0.7,
                                                                px: 1,
                                                                width: 180,
                                                                borderRadius: 2,
                                                                border: '1px solid #A3A3A3!important'
                                                            }}
                                                            placeholder="Enter Count"
                                                            type='number'
                                                            disabled={isExamEnabled || isLocked ? true : false}
                                                            value={configuration.numberOfHardQuestions}
                                                            onChange={(e) => handleNumberOfQuestionsChange(e, 'numberOfHardQuestions')}
                                                            onWheel={(e) => e.target.blur()}
                                                        />
                                                        {errors.numberOfHardQuestions && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                            {errors.numberOfHardQuestions}
                                                        </FormHelperText>}
                                                        {errors?.numberOfQuestions && (
                                                            <FormHelperText sx={{ color: 'error.main' }}>
                                                                {errors.numberOfQuestions}
                                                            </FormHelperText>
                                                        )}
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                </TableRow>
                                            </>
                                            }
                                        </>
                                        : null
                                    }
                                    <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Number of Sets
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <InputBase
                                                sx={{
                                                    py: 0.7,
                                                    px: 1,
                                                    width: 180,
                                                    borderRadius: 2,
                                                    border: '1px solid #A3A3A3!important'
                                                }}
                                                placeholder="Enter Count"
                                                type='number'
                                                disabled={isExamEnabled || isLocked ? true : false}
                                                value={configuration.numberOfSets}
                                                onChange={handleNumberOfSetsChange}
                                                onWheel={(e) => e.target.blur()}
                                            />
                                            {errors?.numberOfSets && (
                                                <FormHelperText sx={{ color: 'error.main' }}>
                                                    {errors.numberOfSets}
                                                </FormHelperText>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{
                                            textAlign: 'right',
                                            color: 'primary.main'
                                        }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.numberOfSets} Sets
                                            </Typography>
                                        </TableCell>
                                    </TableRow>

                                    {configuration.examType !== 'TYPING' && <TableRow>
                                        <TableCell>
                                            <Typography component='span' variant='subtitle1'>
                                                Total Marks
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <InputBase
                                                sx={{
                                                    py: 0.7,
                                                    px: 1,
                                                    width: 180,
                                                    borderRadius: 2,
                                                    border: '1px solid'
                                                }}
                                                placeholder="Enter Count"
                                                type='number'
                                                value={configuration.maxMarks}
                                                disabled={isExamEnabled || contentUploaded || isLocked ? true : false}
                                                onChange={handleMaxMarksChange}
                                                onWheel={(e) => e.target.blur()}
                                            />
                                            {errors.maxMarks && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                {errors.maxMarks}
                                            </FormHelperText>}
                                        </TableCell>
                                        <TableCell sx={{
                                            textAlign: 'right',
                                            color: 'primary.main'
                                        }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.maxMarks} Marks
                                            </Typography>
                                        </TableCell>
                                    </TableRow>}


                                    {configuration.examType === 'MCQ' && (
                                        <Fragment>
                                            <TableRow >
                                                <TableCell>
                                                    <Typography component='span' variant='subtitle1'>
                                                        Randomize Questions Order
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <RadioGroup row name="exam-type"
                                                        onChange={(e, value) => handleConfigChange('randomizeQuestions', value)}>
                                                        <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                            <Radio
                                                                disabled={contentUploaded || isExamEnabled ||
                                                                    configuration.selectionType === "random"
                                                                }
                                                                checked={configuration.randomizeQuestions === 'Yes'}
                                                            />
                                                        } label="Yes" />
                                                        <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                            <Radio
                                                                disabled={contentUploaded || isExamEnabled || configuration.selectionType === "random"}
                                                                checked={configuration.randomizeQuestions === 'No'}
                                                            />
                                                        } label="No" />
                                                    </RadioGroup>
                                                </TableCell>
                                                <TableCell sx={{
                                                    textAlign: 'right',
                                                    color: 'primary.main'
                                                }}>
                                                    <Typography component='span' variant='subtitle1'>
                                                        {configuration.randomizeQuestions}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell>
                                                    <Typography component='span' variant='subtitle1'>
                                                        Randomize Options
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <RadioGroup row name="exam-type"
                                                        onChange={(e, value) => handleConfigChange('randomizeOptions', value)}>
                                                        <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                            <Radio
                                                                disabled={contentUploaded || isExamEnabled}
                                                                checked={configuration.randomizeOptions === 'Yes'}
                                                            />
                                                        } label="Yes" />
                                                        <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                            <Radio
                                                                disabled={contentUploaded || isExamEnabled}
                                                                checked={configuration.randomizeOptions === 'No'}
                                                            />
                                                        } label="No" />
                                                    </RadioGroup>
                                                </TableCell>
                                                <TableCell sx={{
                                                    textAlign: 'right',
                                                    color: 'primary.main'
                                                }}>
                                                    <Typography component='span' variant='subtitle1'>
                                                        {configuration.randomizeOptions}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        </Fragment>
                                    )}

                                    <TableRow>
                                        <TableCell sx={{ border: 'none' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                Multilingual content
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ border: 'none' }}>
                                            <RadioGroup row name="isBilingual" onChange={handleIsBilingualChange}>
                                                <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                    <Radio
                                                        disabled={isExamEnabled || contentUploaded || isLocked ? true : false}
                                                        checked={configuration.isBilingual === 'Yes'}
                                                    />
                                                } label="Yes" />
                                                <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                    <Radio
                                                        disabled={isExamEnabled || contentUploaded || isLocked ? true : false}
                                                        checked={configuration.isBilingual === 'No'}
                                                    />
                                                } label="No" />
                                            </RadioGroup>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', color: 'primary.main', border: 'none' }}>
                                            <Typography component='span' variant='subtitle1'>
                                                {configuration.isBilingual}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ pt: 0 }}>
                                            <Typography component='span' variant='subtitle1'>
                                                Select Languages
                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ pt: 0 }}>
                                            {configuration.isBilingual === 'No' && (
                                                <FormControl sx={{ width: '100%' }}>
                                                    <InputLabel id="bilingualLabel">Languages</InputLabel>
                                                    <Select
                                                        labelId="bilingualLabel"
                                                        id="bilingualSingleSelect"
                                                        sx={{
                                                            width: '100%',
                                                            maxWidth: 320,
                                                            textTransform: 'capitalize',
                                                        }}
                                                        disabled={isExamEnabled || contentUploaded || isLocked ? true : false}
                                                        value={configuration.languages[0] ?? ''}
                                                        onChange={handleBilingualLanguageChange}
                                                        input={
                                                            <OutlinedInput label='Languages' />
                                                        }
                                                    >
                                                        <MenuItem value=''>None</MenuItem>
                                                        {languages.map((language) => {
                                                            return <MenuItem sx={{
                                                                textTransform: 'capitalize'
                                                            }}
                                                                key={language}
                                                                value={language}
                                                            >
                                                                {language}
                                                            </MenuItem>
                                                        })}
                                                    </Select>
                                                    {languagesError && (
                                                        <FormHelperText sx={{ color: 'error.main' }}>
                                                            Please select at least one language
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}
                                            {configuration.isBilingual === 'Yes' && (
                                                <FormControl sx={{ width: '100%' }}>
                                                    <InputLabel id="bilingualMultiLabel">Languages</InputLabel>
                                                    <Select
                                                        labelId="bilingualMultiLabel"
                                                        id="bilingualMultiSelect"
                                                        multiple
                                                        sx={{
                                                            width: '100%',
                                                            maxWidth: 320
                                                        }}
                                                        disabled={isExamEnabled || contentUploaded || isLocked ? true : false}
                                                        value={configuration.languages}
                                                        onChange={handleBilingualLanguagesChange}
                                                        input={
                                                            <OutlinedInput id="select-multiple-chip" label='Languages' />
                                                        }
                                                        renderValue={(selected) => <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                                            {selected.map((value) => (
                                                                <Box key={value} sx={{
                                                                    display: 'flex',
                                                                    gap: 1.5,
                                                                    bgcolor: 'primary.50',
                                                                    py: 0.5,
                                                                    px: 1.5,
                                                                    borderRadius: 1,
                                                                    textTransform: 'capitalize',
                                                                }}>
                                                                    <span>{value}</span>
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                        }
                                                    >
                                                        {languages.map((language) => (
                                                            <MenuItem sx={{
                                                                textTransform: 'capitalize'
                                                            }}
                                                                key={language}
                                                                value={language}
                                                            >
                                                                {language}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                    {languagesError && (
                                                        <FormHelperText sx={{ color: 'error.main' }}>
                                                            Please select at least two language
                                                        </FormHelperText>
                                                    )}
                                                </FormControl>
                                            )}

                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'right', color: 'primary.main', pt: 0 }}>
                                            <Typography component='span' variant='subtitle1' sx={{ textTransform: 'capitalize' }}>
                                                {configuration.languages.join(', ')}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>


                                    {configuration.examType !== 'DESCRIPTIVE'
                                        && <TableRow>
                                            <TableCell>
                                                <Typography component='span' variant='subtitle1'>
                                                    All Questions Mandatory
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <RadioGroup row name="instructionSheet" onChange={(e, value) => handleConfigChange('allQuestionsMandatory', value)}>
                                                    <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                        <Radio
                                                            disabled={isExamEnabled ? true : false}
                                                            checked={configuration.allQuestionsMandatory === 'Yes'}
                                                        />
                                                    } label="Yes" />
                                                    <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                        <Radio
                                                            disabled={isExamEnabled ? true : false}
                                                            checked={configuration.allQuestionsMandatory === 'No'}
                                                        />
                                                    } label="No" />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{
                                                textAlign: 'right',
                                                color: 'primary.main',
                                            }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    {configuration.allQuestionsMandatory}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    }

                                    {configuration.examType !== 'DESCRIPTIVE'
                                        && < TableRow >
                                            <TableCell>
                                                <Typography component='span' variant='subtitle1'>
                                                    Only Forward Question Navigation?
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <RadioGroup row name="instructionSheet" onChange={(e, value) => handleConfigChange('questionNavigationForwardOnly', value)}>
                                                    <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                        <Radio
                                                            disabled={isExamEnabled ? true : false}
                                                            checked={configuration.questionNavigationForwardOnly === 'Yes'}
                                                        />
                                                    } label="Yes" />
                                                    <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                        <Radio
                                                            disabled={isExamEnabled ? true : false}
                                                            checked={configuration.questionNavigationForwardOnly === 'No'}
                                                        />
                                                    } label="No" />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{
                                                textAlign: 'right',
                                                color: 'primary.main',
                                            }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    {configuration.questionNavigationForwardOnly}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    }

                                    {configuration.examType !== 'DESCRIPTIVE'
                                        && <TableRow>
                                            <TableCell>
                                                <Typography component='span' variant='subtitle1'>
                                                    Negative Marking
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <RadioGroup row name="instructionSheet" onChange={(e, value) => handleConfigChange('negativeMarking', value)}>
                                                    <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                        <Radio
                                                            disabled={contentUploaded || isExamEnabled}
                                                            checked={configuration.negativeMarking === 'Yes'}
                                                        />
                                                    } label="Yes" />
                                                    <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                        <Radio
                                                            disabled={contentUploaded || isExamEnabled}
                                                            checked={configuration.negativeMarking === 'No'}
                                                        />
                                                    } label="No" />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{
                                                textAlign: 'right',
                                                color: 'primary.main',
                                            }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    {configuration.negativeMarking}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    }

                                    {configuration.examType === 'DESCRIPTIVE'
                                        ? <TableRow>
                                            <TableCell>
                                                <Typography component='span' variant='subtitle1'>
                                                    Number of Answer Pages
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <InputBase
                                                    sx={{
                                                        py: 0.7,
                                                        px: 1,
                                                        width: 180,
                                                        borderRadius: 2,
                                                        border: '1px solid #A3A3A3!important'
                                                    }}
                                                    placeholder="Enter Count"
                                                    type='number'
                                                    disabled={isExamEnabled || isLocked ? true : false}
                                                    value={configuration.numberOfAnswerPages}
                                                    onChange={handleNumberOfAnswerPagesChange}
                                                    onWheel={(e) => e.target.blur()}
                                                />
                                                {errors.numberOfAnswerPages && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                    {errors.numberOfAnswerPages}
                                                </FormHelperText>}
                                            </TableCell>
                                            <TableCell sx={{
                                                textAlign: 'right',
                                                color: 'primary.main'
                                            }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    {configuration.numberOfAnswerPages} Pages
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        : null}


                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CustomTabPanel>
                    {configuration.examType !== 'DESCRIPTIVE'
                        && <CustomTabPanel value={tabValue} index={2}>
                            <TableContainer sx={{
                                p: 2,
                                pt: 0.25,
                                maxHeight: 'inherit',
                                maxWidth: 'inherit',
                                // overflow: 'auto',
                                borderRadius: 1
                            }}>
                                <Table stickyHeader aria-label="table">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>
                                                <Typography component='span' variant='subtitle1'>
                                                    Instant Score
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <RadioGroup row name="instant-score" onChange={(e, value) => handleConfigChange('instantScore', value)}>
                                                    <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                        <Radio
                                                            disabled={isExamEnabled ? true : false}
                                                            checked={configuration.instantScore === 'Yes'}
                                                        />
                                                    } label="Yes" />
                                                    <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                        <Radio
                                                            disabled={isExamEnabled ? true : false}
                                                            checked={configuration.instantScore === 'No'}
                                                        />
                                                    } label="No" />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', color: 'primary.main' }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    {configuration.instantScore}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>

                                        <TableRow>
                                            <TableCell sx={{ borderColor: configuration.instantResult === 'No' ? '' : 'transparent !important' }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    ⁠   Instant Result
                                                    <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ borderColor: configuration.instantResult === 'No' ? '' : 'transparent !important' }}>
                                                <RadioGroup row name="instant-result" onChange={(e, value) => handleConfigChange('instantResult', value)}>
                                                    <FormControlLabel sx={{ width: 165 }} value='Yes' control={
                                                        <Radio
                                                            disabled={isExamEnabled ? true : false}
                                                            checked={configuration.instantResult === 'Yes'}
                                                        />
                                                    } label="Yes" />
                                                    <FormControlLabel sx={{ width: 165 }} value='No' control={
                                                        <Radio
                                                            disabled={isExamEnabled ? true : false}
                                                            checked={configuration.instantResult === 'No'}
                                                        />
                                                    } label="No" />
                                                </RadioGroup>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'right', color: 'primary.main', borderColor: configuration.instantResult === 'No' ? '' : 'transparent !important' }}>
                                                <Typography component='span' variant='subtitle1'>
                                                    {configuration.instantResult}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        {configuration.instantResult === 'Yes' && (
                                            <TableRow sx={{ border: 'none' }}>
                                                <TableCell sx={{ pt: 0 }} >
                                                    <Typography component='span' variant='subtitle1'>
                                                        Passing Marks
                                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ pt: 0 }}>
                                                    <InputBase
                                                        sx={{
                                                            py: 0.7,
                                                            px: 1,
                                                            width: 180,
                                                            borderRadius: 2,
                                                            border: '1px solid'
                                                        }}
                                                        placeholder="Enter Min Marks"
                                                        type='number'
                                                        value={configuration.passingMarks}
                                                        disabled={isLocked ? true : false}
                                                        onChange={handlePassingMarksChange}
                                                        onWheel={(e) => e.target.blur()}
                                                    />
                                                    {errors.passingMarks && <FormHelperText sx={{ color: 'error.main', maxWidth: '100%' }}>
                                                        {errors.passingMarks}
                                                    </FormHelperText>}
                                                </TableCell>
                                                <TableCell sx={{ textAlign: 'right', color: 'primary.main', pt: 0 }}>
                                                    <Typography component='span' variant='subtitle1'>
                                                        {configuration.passingMarks} Marks
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CustomTabPanel>}

                    <Paper sx={{
                        borderRadius: 1,
                        bgcolor: 'background.base',
                        flex: 1,
                        maxWidth: 'calc(100vw - 140px)',
                        overflow: 'auto',
                    }}>
                    </Paper>
                </Box>
            </Container>

            <Modal open={lockedSaveConfirmVisible} hideBackdrop={true} className='backdrop' onClose={() => setLockedSaveConfirmVisible(false)}>
                <Box className='modal-container' sx={{
                    maxWidth: 720,
                    p: 9,
                    textAlign: 'center',
                }}>
                    <Typography variant='h6' sx={{ fontWeight: 400, mb: 2 }}>
                        This exam is currently running. Are you sure you want to update the configuration?
                    </Typography>
                    {/* <Typography variant='body2' sx={{ mb: 8, color: 'text.secondary' }}>
                        Only the following fields will be updated: Exam Times, Maximum Login Attempts, Login Before Minutes, and Late Login settings.
                    </Typography> */}
                    <div className='actions-container' style={{ justifyContent: 'center', gap: '40px' }}>
                        <RadiusButton variant='outlined' onClick={() => setLockedSaveConfirmVisible(false)}>
                            Cancel
                        </RadiusButton>
                        <RadiusButton variant='contained' onClick={handleConfirmLockedSave}>
                            Confirm
                        </RadiusButton>
                    </div>
                </Box>
            </Modal>

        </MainContainer >
    );
}